import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ImportFromQueueDialog from '../components/ImportFromQueueDialog';
import ScanArpDialog from '../components/ScanArpDialog';
import { safeTrim } from '../utils/validation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Wifi, WifiOff, Power, PowerOff, ArrowUpDown, ArrowUp, ArrowDown, Shield, ShieldOff, Activity, Server, Ban, CheckCircle, Eye, Key, MonitorPlay, TrendingUp, Download, Upload, ToggleLeft, ToggleRight, AlertTriangle, MoreHorizontal, UserPlus, CreditCard, CircleDollarSign, Link, FileText, Calendar, RefreshCw, ExternalLink, Copy, Signal, X, Menu, ChevronDown, Clock, UserCircle, MessageCircleOff } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import CustomerFormTabs from '../components/CustomerFormTabs';
import CustomerMobileCards from '../components/customers/CustomerMobileCards';
import CustomerDesktopTable from '../components/customers/CustomerDesktopTable';
import CustomerListTabs from '../components/customers/CustomerListTabs';
import StoppedCustomersPanel from '../components/customers/StoppedCustomersPanel';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import ErrorBoundary from '../components/ErrorBoundary';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACS_URL = process.env.REACT_APP_ACS_URL || '';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const Customers = ({ filter = null }) => {
  // AUTOSET_STATIC_FROM_FILTER: when navigated to /customers/dhcp, default connection_type='Static'
  const _filterDefaultConnType = filter === 'dhcp' ? 'Static' : (filter === 'hotspot' ? 'Hotspot' : 'PPPoE');
  // PRESET_FORM_CONN_TYPE: ensure formData.connection_type defaults to the filter type
  // (Static when on /customers/dhcp, Hotspot for /customers/hotspot, else PPPoE).


  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 2026-07-30: Persist list state (search / filter / pagination / last-viewed row)
  // so that returning from CustomerDetail via "Back to Customers" restores
  // the exact same list context AND scrolls + flashes the row we just viewed.
  const _listStateKey = `customers_list_state_${filter || 'pppoe'}`;
  const _rsRef = React.useRef(null);
  if (_rsRef.current === null) {
    try {
      const raw = sessionStorage.getItem(_listStateKey);
      if (raw) {
        _rsRef.current = JSON.parse(raw) || {};
        sessionStorage.removeItem(_listStateKey);
      } else {
        _rsRef.current = {};
      }
    } catch (_e) { _rsRef.current = {}; }
  }
  const _rs = _rsRef.current || {};

  const { t, selectedBranch, setSelectedBranch, selectedRouter } = useApp();
  const { user } = useAuth();
  const { branding } = useBranding();
  const [customers, setCustomers] = useState([]);
  const [ipConflictCount, setIpConflictCount] = useState({ ips: 0, customers: 0 });

  // FIX 2026-05-16: Subscribe to realtime PPPoE events via SSE.
  // Updates is_online + current_ip of matching customer rows live.
  useEffect(() => {
    const url = `${API_URL}/api/mikrotik/pppoe-events-stream`;
    let es;
    let reconnectTimer;
    const connect = () => {
      try {
        es = new EventSource(url, { withCredentials: false });
      } catch (e) {
        console.warn('[SSE] EventSource init failed:', e);
        return;
      }
      es.addEventListener('pppoe', (e) => {
        try {
          const data = JSON.parse(e.data);
          if (!data?.username) return;
          const uname = String(data.username).toLowerCase();
          setCustomers((prev) => prev.map((c) => {
            if (String(c.pppoe_username || '').toLowerCase() === uname) {
              return {
                ...c,
                is_online: data.is_online ? 1 : 0,
                current_ip: data.is_online ? (data.ip_address || c.current_ip) : null,
                current_mac: data.is_online ? (data.mac_address || c.current_mac) : null,
                // ACTIVEPPPOEIP_2026-09-04: sinkron IP realtime dari event pppoe
                active_pppoe_ip: data.is_online ? (data.ip_address || c.active_pppoe_ip) : null,
                last_online: data.is_online ? new Date().toISOString() : c.last_online,
              };
            }
            return c;
          }));
        } catch (err) {
          console.warn('[SSE] parse error', err);
        }
      });
      es.onerror = () => {
        try { es.close(); } catch {}
        reconnectTimer = setTimeout(connect, 5000);
      };
    };
    connect();
    return () => {
      try { es && es.close(); } catch {}
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);
  const [branches, setBranches] = useState([]);
  const [owners, setOwners] = useState([]);
  // 2026-06-02: UI density preference (compact / comfortable) — persisted to localStorage
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('customers_density') || 'compact'; } catch (e) { return 'compact'; }
  });
  const toggleDensity = () => {
    const next = density === 'compact' ? 'comfortable' : 'compact';
    setDensity(next);
    try { localStorage.setItem('customers_density', next); } catch (e) { /* ignore */ }
  };
  const [servicePlans, setServicePlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState(_rs.searchTerm ?? '');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // Increased debounce
  const branchFilter = selectedBranch || 'all';
  const routerFilter = selectedRouter || 'all';
  const ownerFromUrl = searchParams.get('owner');
  const [ownerFilter, setOwnerFilter] = useState(_rs.ownerFilter ?? (ownerFromUrl || 'all'));
  const [paymentTypeFilter, setPaymentTypeFilter] = useState(_rs.paymentTypeFilter ?? 'all');
  
  // 2026-05-20: hide owner/branch dropdown utk role di luar admin/super_admin
  const _userRoleLower = user?.role?.toLowerCase();
  const isRestrictedRole = ['owner', 'kepala_cabang', 'customer service', 'cs', 'teknisi'].includes(_userRoleLower);
  // 2026-05-23: Kepala Cabang now KEEPS owner filter visible (they manage
  // multiple owners within their branch). Hide only for true single-owner
  // roles + lower staff (CS / Teknisi).
  const isOwnerRole = ['owner', 'customer service', 'cs', 'teknisi'].includes(_userRoleLower);
  const [connectionTypeFilter, setConnectionTypeFilter] = useState(filter);
  // Read ?status=online|offline from URL (used by dashboard segment-card click-through)
  const statusFromUrl = searchParams.get('status');
  const [onlineFilter, setOnlineFilter] = useState(
    _rs.onlineFilter ?? (statusFromUrl === 'online' || statusFromUrl === 'offline' ? statusFromUrl : 'all')
  );
  // 2026-05-31: read ?isolir=isolated from URL (used by dashboard Diisolir card)
  const isolirFromUrl = searchParams.get('isolir');
  const [isolirFilter, setIsolirFilter] = useState(
    _rs.isolirFilter ?? (isolirFromUrl === 'isolated' || isolirFromUrl === 'active' ? isolirFromUrl : 'all')
  );  // Filter for isolated customers
  const [weakSignalFilter, setWeakSignalFilter] = useState(_rs.weakSignalFilter ?? false);  // Quick filter: rx_power < -25 dBm
  // 2026-02-13: payment status filter (Lunas vs Belum Lunas) — sent to backend as ?payment_status=
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(_rs.paymentStatusFilter ?? 'all');
  const [registrationPeriodFilter, setRegistrationPeriodFilter] = useState(_rs.registrationPeriodFilter ?? null); // REGISTRATION_FILTER_STATE_V1
  const [gracePeriodFilter, setGracePeriodFilter] = useState(_rs.gracePeriodFilter ?? false); // GRACE_PERIOD_FILTER_STATE_V1: 'month' | null
  const [waInactiveFilter, setWaInactiveFilter] = useState(_rs.waInactiveFilter ?? false); // WA_INACTIVE_FILTER_V1 (Delta #72)
  const [listTab, setListTab] = useState(() => {
    if (searchParams.get('tab') === 'berhenti') return 'stopped';
    try { return sessionStorage.getItem('customers_list_tab') === 'stopped' ? 'stopped' : 'all'; } catch (_e) { return 'all'; }
  }); // STOPPED_TAB_V1 (Delta #79)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [scanIsolirOpen, setScanIsolirOpen] = useState(false);
  const [scanArpOpen, setScanArpOpen] = useState(false);
  const [importQueueOpen, setImportQueueOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [sortConfig, setSortConfig] = useState(_rs.sortConfig ?? { key: 'updated_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(_rs.currentPage ?? 1);
  // last-viewed row (single-use highlight after list load)
  const [flashCustomerId, setFlashCustomerId] = useState(_rs.viewedCustomerId ?? null);
  const [itemsPerPage, setItemsPerPage] = useState(_rs.itemsPerPage ?? 10);
  // 2026-07-15: counter Online/Offline yang visible + clickable di header
  const [onlineCounts, setOnlineCounts] = useState({ online: 0, offline: 0, registered_this_month: 0, renewal_this_month: 0, isolated_count: 0, grace_period_count: 0, loading: false }); // STAT_CARDS_ROW_V1
  const [totalCount, setTotalCount] = useState(0);

  // 2026-07-30: Debt (berhutang) feature — modal & state
  const [debtModalOpen, setDebtModalOpen] = useState(false);
  const [debtCustomer, setDebtCustomer] = useState(null);
  const [debtDeadline, setDebtDeadline] = useState('');
  const [debtNote, setDebtNote] = useState('');
  const [debtLoading, setDebtLoading] = useState(false);

  // 2026-07-30: Save current list state to sessionStorage so that "Back to
  // Customers" from CustomerDetail lands the user on the same tab / filter /
  // page and briefly highlights the row they just visited.
  const _saveListState = useCallback((viewedId) => {
    try {
      sessionStorage.setItem(_listStateKey, JSON.stringify({
        searchTerm,
        ownerFilter,
        paymentTypeFilter,
        paymentStatusFilter,
        onlineFilter,
        isolirFilter,
        weakSignalFilter,
        registrationPeriodFilter,
        gracePeriodFilter,
        waInactiveFilter,
        sortConfig,
        currentPage,
        itemsPerPage,
        viewedCustomerId: viewedId,
      }));
    } catch (_e) { /* sessionStorage might be blocked; non-fatal */ }
  }, [_listStateKey, searchTerm, ownerFilter, paymentTypeFilter, paymentStatusFilter, onlineFilter, isolirFilter, weakSignalFilter, registrationPeriodFilter, gracePeriodFilter, waInactiveFilter, sortConfig, currentPage, itemsPerPage]);

  const handleViewDetail = useCallback((customerId) => {
    _saveListState(customerId);
    navigate(`/app/customers/${customerId}`);
  }, [_saveListState, navigate]);

  // Reset branch to "Semua Cabang" when PPPoE/Customers page is loaded
  useEffect(() => {
    setSelectedBranch('all');
  }, [setSelectedBranch]);

  // 2026-07-30: Scroll to + flash-highlight the last-viewed customer row when
  // the current customers list contains it (after restoring persisted state).
  useEffect(() => {
    if (!flashCustomerId) return;
    if (!customers || customers.length === 0) return;
    const targetId = String(flashCustomerId);
    const match = customers.find((c) => String(c.id) === targetId);
    if (!match) { setFlashCustomerId(null); return; }
    // Defer to next tick so the DOM has painted the row.
    const t0 = setTimeout(() => {
      const el = document.querySelector('[data-customer-row-id="' + targetId + '"]');
      if (!el) return;
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_e) {}
      el.classList.add('customer-row-flash');
      setTimeout(() => {
        try { el.classList.remove('customer-row-flash'); } catch (_e) {}
        setFlashCustomerId(null);
      }, 2600);
    }, 60);
    return () => clearTimeout(t0);
  }, [flashCustomerId, customers]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [acsDevicesStatus, setAcsDevicesStatus] = useState({});
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  // 2026-06-12: bulk Tandai Lunas (PPPoE page)
  const [bulkPaidDialogOpen, setBulkPaidDialogOpen] = useState(false);
  const [bulkPaidLoading, setBulkPaidLoading] = useState(false);
  const [bulkPaidForm, setBulkPaidForm] = useState({ calculationBase: 'due_date', paymentDate: new Date().toISOString().slice(0, 10) });
  const [bulkPaidResult, setBulkPaidResult] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bulkDeletePreview, setBulkDeletePreview] = useState(null);
  const [loadingBulkPreview, setLoadingBulkPreview] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [customerOnlineStatus, setCustomerOnlineStatus] = useState({});
  const [mikrotikLoading, setMikrotikLoading] = useState({});
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedCustomerForReset, setSelectedCustomerForReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showBandwidthDialog, setShowBandwidthDialog] = useState(false);
  const [bandwidthData, setBandwidthData] = useState(null);
  const [bandwidthLoading, setBandwidthLoading] = useState(false);
  const [customersBandwidth, setCustomersBandwidth] = useState({}); // Store bandwidth for all customers
  const [showOnboardingWarning, setShowOnboardingWarning] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  
  // Import Modal State
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Sync Status State
  const [syncingStatus, setSyncingStatus] = useState(false);
  
  // Ubah ke Lunas Dialog State
  const [showLunasDialog, setShowLunasDialog] = useState(false);
  const [lunasCustomer, setLunasCustomer] = useState(null);
  const [lunasDateOption, setLunasDateOption] = useState('due_date'); // 'due_date' or 'payment_date'
  const [lunasPaymentDate, setLunasPaymentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [lunasLoading, setLunasLoading] = useState(false);

  // Function to sync online status from RADIUS
  const syncOnlineStatus = async () => {
    setSyncingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/pppoe-monitoring/sync-online-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`Sync berhasil! ${response.data.online_count} pelanggan online dari ${response.data.total_from_mikrotik} session MikroTik`);
        // Refresh customer list
        fetchCustomers();
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Gagal sync status: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncingStatus(false);
    }
  };

  // Function to get branch name from branch_id
  const getBranchName = (branchId, branchName = null) => {
    // Use branch_name from API response if available (faster)
    if (branchName) return branchName;
    // Fallback to lookup from branches array
    if (!branchId) return '-';
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : '-';
  };

  // Function to get badge color based on package name
  const getPackageColor = (packageName) => {
    const name = packageName.toUpperCase();
    
    if (name.includes('NEW GOLD') || name.includes('GOLD 250')) {
      return {
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        border: 'border-amber-300'
      };
    } else if (name.includes('GOLD')) {
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-300'
      };
    } else if (name.includes('SILVER')) {
      return {
        bg: 'bg-slate-100',
        text: 'text-slate-700',
        border: 'border-slate-300'
      };
    } else if (name.includes('PLATINUM') || name.includes('PREMIUM')) {
      return {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        border: 'border-purple-300'
      };
    } else if (name.includes('BRONZE') || name.includes('BASIC')) {
      return {
        bg: 'bg-indigo-100',
        text: 'text-indigo-800',
        border: 'border-indigo-300'
      };
    } else {
      return {
        bg: 'bg-indigo-50',
        text: 'text-indigo-700',
        border: 'border-indigo-200'
      };
    }
  };

  // Function to get payment status badge color
  const getPaymentStatusColor = (status) => {
    if (status === 'LUNAS') {
      return {
        bg: 'bg-emerald-100',
        text: 'text-emerald-800',
        border: 'border-emerald-300'
      };
    } else if (status === 'TERLAMBAT') {
      return {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-300'
      };
    } else {
      // BELUM LUNAS
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-300'
      };
    }
  };

  // Function to get payment status text based on language
  const getPaymentStatusText = (status) => {
    if (status === 'LUNAS') {
      return t('paidStatus');
    } else if (status === 'TERLAMBAT') {
      return t('overdueStatus');
    } else {
      return t('unpaidStatus');
    }
  };
  const [formData, setFormData] = useState({
    // Auto-generated
    account_number: '',
    // Tab 1: Info Pelanggan
    name: '',
    email: '',
    phone: '',
    address: '',
    nik: '',
    longitude: '',
    latitude: '',
    ip_address: 'Automatic',
    owner: '',
    branch_id: '',
    note: '',
    // Password Portal (untuk login customer portal)
    password: '12345678', // Default password
    // Tab 2: Paket Langganan
    service_plan: '',
    connection_type: filter === 'dhcp' ? 'Static' : (filter === 'hotspot' ? 'Hotspot' : 'PPPoE'), // Initial from URL filter
    connection_type_set_by_filter: false, // Flag to track if set by filter
    registration_status: 'aktif', // Default: Aktif Sekarang
    customer_type: 'reguler', // Default: Reguler
    payment_type: 'prepaid', // Default: Prepaid
    payment_status: 'sudah_bayar', // Default: Sudah Bayar
    account_status: 'enabled',
    bind_on_login: false,
    monthly_fee: 0,
    installation_date: new Date().toISOString().split('T')[0],
    // Tab 3: Info Billing
    bill_ppn: false,
    tax_percentage: 0,
    auto_prorate: false,
    prorate_date: null,
    prorate_days_remaining: 0,
    activate_promo: false,
    promo_percentage: 0,
    promo_duration_months: 0,
    final_invoice_price: 0,
    installation_fee: 0,
    seller_fee: 0,
    device_rental: 0,
    other_fees: 0,
    // Mikrotik (moved to bottom)
    mikrotik_user: '',
    mikrotik_password: '',
    create_mikrotik_user: false,
    // Static IP fields (when connection_type='static')
    static_ip: '',
    static_mac: '',
    static_gateway: '',
    static_interface: '',
    // Tab 4: Perangkat (2026-07-05)
    device_brand: '',
    device_model: '',
    device_sn: '',
    device_band: '',
    device_photo_url: ''
  });
  // Auto-preset connection_type whenever it is blank (e.g. fresh form load on Statik/DHCP page)
  React.useEffect(() => {
    setFormData(prev => {
      if (!prev.connection_type || prev.connection_type === '') {
        return { ...prev, connection_type: _filterDefaultConnType, connection_type_set_by_filter: true };
      }
      return prev;
    });
  }, [filter]);


  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      account_number: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      nik: '',
      longitude: '',
      latitude: '',
      ip_address: 'Automatic',
      owner: '',
      branch_id: '',
      note: '',
      password: '12345678',
      service_plan: '',
      connection_type: filter === 'dhcp' ? 'Static' : (filter === 'hotspot' ? 'Hotspot' : 'PPPoE'), // Initial from URL filter
      connection_type_set_by_filter: false, // Reset flag
      registration_status: 'aktif',
      customer_type: 'reguler',
      payment_type: 'prepaid',
      payment_status: 'sudah_bayar',
      account_status: 'enabled',
      bind_on_login: false,
      monthly_fee: 0,
      installation_date: new Date().toISOString().split('T')[0],
      bill_ppn: false,
      tax_percentage: 0,
      auto_prorate: false,
      prorate_date: null,
      prorate_days_remaining: 0,
      activate_promo: false,
      promo_percentage: 0,
      promo_duration_months: 0,
      final_invoice_price: 0,
      installation_fee: 0,
      seller_fee: 0,
      device_rental: 0,
      other_fees: 0,
      mikrotik_user: '',
      mikrotik_password: '',
      create_mikrotik_user: false,
      static_ip: '',
      static_mac: '',
      static_gateway: '',
      static_interface: ''
    });
    setEditingCustomerId(null);
  };

  useEffect(() => {
    fetchBranches();
    fetchOwners();
    fetchServicePlans();
  }, []); // Only run once on mount

  // 2026-02: Voice Command listener - dipicu oleh VoiceCommandListener global
  // saat user mengucapkan frasa seperti "tambah pelanggan".
  useEffect(() => {
    const handler = () => {
      setIsEditMode(false);
      resetForm();
      setIsDialogOpen(true);
    };
    window.addEventListener('voice-command:add-customer', handler);
    return () => window.removeEventListener('voice-command:add-customer', handler);
  }, []);

  // 2026-02: Voice Command listener untuk pencarian pelanggan.
  // Dipicu saat user ucap "cari pelanggan [nama]".
  useEffect(() => {
    const handler = (e) => {
      const q = (e?.detail?.query || '').trim();
      if (!q) return;
      setSearchTerm(q);
      setCurrentPage(1);
      // Smooth scroll & focus search box untuk feedback visual
      setTimeout(() => {
        const el = document.querySelector('[data-testid="search-input"]');
        if (el) {
          try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
          try { el.focus(); } catch (_) {}
        }
      }, 100);
    };
    window.addEventListener('voice-command:search-customer', handler);
    return () => window.removeEventListener('voice-command:search-customer', handler);
  }, []);

  // Refetch IP conflicts when branch context changes
  useEffect(() => {
    fetchIpConflicts();
  }, [selectedBranch, selectedRouter]);

  const fetchIpConflicts = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = (selectedBranch && selectedBranch !== 'all') ? { branch_id: selectedBranch } : {};
      const res = await axios.get(API_URL + '/api/v2/customers/ip-conflicts', {
        headers: { Authorization: 'Bearer ' + token },
        params,
      });
      if (res.data?.success) {
        setIpConflictCount({
          ips: res.data.total_conflict_ips || 0,
          customers: res.data.total_conflict_customers || 0,
        });
      }
    } catch (e) {
      // silent fail
    }
  };

  // Fetch customers when filters or pagination change
  useEffect(() => {
    // 2026-08-08 FIX: AbortController pattern — cancel fetch lama saat dependency
    // berubah supaya tidak ada race condition (fetch besar tanpa filter overwrite
    // fetch kecil dengan filter, seperti bug di Invoices.jsx).
    const controller = new AbortController();
    fetchCustomers({ signal: controller.signal });
    return () => controller.abort();
  }, [currentPage, itemsPerPage, filter, connectionTypeFilter, debouncedSearchTerm, branchFilter, routerFilter, ownerFilter, onlineFilter, isolirFilter, weakSignalFilter, registrationPeriodFilter, gracePeriodFilter, waInactiveFilter, paymentTypeFilter, paymentStatusFilter]);

  // 2026-07-15: fetch counter Online/Offline (only when filter changes, not on pagination)
  // 2026-07-31 FIX: reset counter values to 0 saat loading agar tidak tampil angka stale
  // dari filter sebelumnya (mis. saat pindah menu PPPoE -> Hotspot component tidak unmount)
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setOnlineCounts({ online: 0, offline: 0, sessions: null, debt: 0, registered_this_month: 0, renewal_this_month: 0, isolated_count: 0, grace_period_count: 0, loading: true });
        const token = localStorage.getItem('token');
        const buildParams = (isOnline) => {
          const p = new URLSearchParams();
          p.append('limit', '1');
          p.append('page', '1');
          p.append('is_online', isOnline);
          if (connectionTypeFilter && connectionTypeFilter !== 'all') p.append('connection_type', connectionTypeFilter);
          if (branchFilter && branchFilter !== 'all') p.append('branch_id', branchFilter);
          if (routerFilter && routerFilter !== 'all') p.append('router_id', routerFilter);
          if (ownerFilter && ownerFilter !== 'all') p.append('owner_id', ownerFilter);
          if (paymentTypeFilter && paymentTypeFilter !== 'all') p.append('payment_type', paymentTypeFilter);
          if (debouncedSearchTerm) p.append('search', debouncedSearchTerm);
          if (isolirFilter === 'isolated') p.append('is_isolated', '1');
          else if (isolirFilter === 'active') p.append('is_isolated', '0');
          return p;
        };
        // 2026-07-31: Also fetch live RADIUS session count via /session-count endpoint.
        // Ini paling berguna untuk Hotspot — session bisa termasuk voucher/orphan.
        const sesParams = new URLSearchParams();
        if (connectionTypeFilter && connectionTypeFilter !== 'all') sesParams.append('connection_type', connectionTypeFilter);
        if (branchFilter && branchFilter !== 'all') sesParams.append('branch_id', branchFilter);
        if (routerFilter && routerFilter !== 'all') sesParams.append('router_id', routerFilter);
        const [rOn, rOff, rSes, rDebt, rReg, rStats] = await Promise.all([
          axios.get(API_URL + '/api/v2/customers?' + buildParams('1').toString(), { headers: { Authorization: 'Bearer ' + token } }),
          axios.get(API_URL + '/api/v2/customers?' + buildParams('0').toString(), { headers: { Authorization: 'Bearer ' + token } }),
          axios.get(API_URL + '/api/v2/customers/session-count?' + sesParams.toString(), { headers: { Authorization: 'Bearer ' + token } }).catch(() => ({ data: {} })),
          axios.get(API_URL + '/api/v2/customers/debt-count?' + sesParams.toString(), { headers: { Authorization: 'Bearer ' + token } }).catch(() => ({ data: {} })),
          axios.get(API_URL + '/api/v2/customers/registered-count?period=month&' + sesParams.toString(), { headers: { Authorization: 'Bearer ' + token } }).catch(() => ({ data: {} })),
          axios.get(API_URL + '/api/v2/customers/dashboard-stats?' + sesParams.toString(), { headers: { Authorization: 'Bearer ' + token } }).catch(() => ({ data: {} })),
        ]);
        setOnlineCounts({
          online: rOn.data?.total || 0,
          offline: rOff.data?.total || 0,
          sessions: rSes.data?.session_count ?? null,
          debt: rDebt.data?.debt_count ?? 0,
          registered_this_month: rReg.data?.registered_count ?? 0,
          renewal_this_month: rStats.data?.renewal_this_month ?? 0,
          isolated_count: rStats.data?.isolated_count ?? 0,
          grace_period_count: rStats.data?.grace_period_count ?? 0,
          loading: false,
        });
      } catch (e) {
        setOnlineCounts(prev => ({ ...prev, loading: false }));
      }
    };
    fetchCounts();
    // Note: intentionally NOT depending on currentPage — counter berdasarkan filter, bukan halaman
  }, [filter, connectionTypeFilter, debouncedSearchTerm, branchFilter, routerFilter, ownerFilter, isolirFilter, paymentTypeFilter]);

  // Reset to page 1 and sync filter when filter prop changes
  useEffect(() => {
    setConnectionTypeFilter(filter);
    setCurrentPage(1);
    // Immediately fetch with new filter
    fetchCustomers();
  }, [filter]);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchOwners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/owners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setOwners(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };

  const fetchServicePlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/service-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setServicePlans(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching service plans:', error);
    }
  };

  const checkSetupProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenant/setup-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error checking setup progress:', error);
      return null;
    }
  };

  // PERFORMANCE: Server-side filtering is now used
  // Only apply client-side sorting on the already filtered data from server
  const sortedCustomers = useMemo(() => {
    let sorted = [...customers];
    
    // Sort (client-side only - server handles filtering)
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // GenieACS group sorting (Model/Signal/ACS online)
        if (sortConfig.key === '_acs_model' || sortConfig.key === '_acs_signal' || sortConfig.key === '_acs_online') {
          const acsA = acsDevicesStatus[a.wan_device_serial || a.wan_device_id] || {};
          const acsB = acsDevicesStatus[b.wan_device_serial || b.wan_device_id] || {};
          if (sortConfig.key === '_acs_model') {
            aValue = (acsA.product_class || a.ont_model || '').toString().toLowerCase();
            bValue = (acsB.product_class || b.ont_model || '').toString().toLowerCase();
          } else if (sortConfig.key === '_acs_signal') {
            // rx_power numeric (dBm). Missing values pushed to bottom.
            const ra = acsA.rx_power != null ? Number(acsA.rx_power) : (a.ont_rx_power != null ? Number(a.ont_rx_power) : null);
            const rb = acsB.rx_power != null ? Number(acsB.rx_power) : (b.ont_rx_power != null ? Number(b.ont_rx_power) : null);
            aValue = (ra == null || isNaN(ra)) ? (sortConfig.direction === 'asc' ? Infinity : -Infinity) : ra;
            bValue = (rb == null || isNaN(rb)) ? (sortConfig.direction === 'asc' ? Infinity : -Infinity) : rb;
          } else { // _acs_online
            aValue = (acsA.online || a.acs_status === 'online') ? 1 : 0;
            bValue = (acsB.online || b.acs_status === 'online') ? 1 : 0;
          }
        }

        // Handle date sorting
        if (sortConfig.key === 'installation_date') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }

        // Handle number sorting
        if (sortConfig.key === 'monthly_fee') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        // Handle string sorting
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = (bValue || '').toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sorted;
  }, [customers, sortConfig, acsDevicesStatus]);

  // Server-side pagination - data already paginated from backend
  const currentCustomers = sortedCustomers;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Helper function to map MySQL response to frontend format
  const mapCustomerFromMySQL = (customer) => {
    // Map payment_status to payment_status_computed
    let paymentStatusComputed = 'BELUM LUNAS';
    if (customer.payment_status === 'sudah_bayar' || customer.payment_status === 'lunas') {
      paymentStatusComputed = 'LUNAS';
    } else if (customer.payment_status === 'terlambat') {
      paymentStatusComputed = 'TERLAMBAT';
    }
    
    return {
      ...customer,
      mikrotik_user: customer.pppoe_username || '',
      mikrotik_password: customer.pppoe_password || '',
      service_plan: customer.service_plan_name || '',
      price: customer.monthly_price || 0,
      monthly_fee: customer.monthly_price || 0,
      owner: customer.owner_name || '',
      is_active: Boolean(customer.is_active),
      payment_status_computed: paymentStatusComputed,
      has_wan_device: Boolean(customer.wan_device_id)
    };
  };

  // 2026-07-28: Optimistic local update helper untuk QuickDueDatePicker
  // Menghindari full window.location.reload() yang lambat (3-8 detik).
  const updateCustomerLocal = React.useCallback((customerId, patch) => {
    setCustomers((prev) => prev.map((c) => (c.id === customerId ? { ...c, ...patch } : c)));
  }, []);

  const fetchCustomers = async ({ signal = null } = {}) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build query params for API v2 (MySQL)
      const params = new URLSearchParams();
      
      // Calculate offset from page
      const offset = (currentPage - 1) * itemsPerPage;
      params.append('limit', itemsPerPage.toString());
      params.append('offset', offset.toString());
      
      // Add search term
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }
      
      // Add branch filter
      if (branchFilter && branchFilter !== 'all') {
        params.append('branch_id', branchFilter);
      }
      // Add router filter (global header dropdown)
      if (routerFilter && routerFilter !== 'all') {
        params.append('router_id', routerFilter);
      }
      
      // Add owner filter
      if (ownerFilter && ownerFilter !== 'all') {
        params.append('owner_id', ownerFilter);
      }

      // 2026-02: payment_type filter (prepaid/postpaid)
      if (paymentTypeFilter && paymentTypeFilter !== 'all') {
        params.append('payment_type', paymentTypeFilter);
      }
      
      // Add connection_type filter (for PPPoE/Hotspot tabs)
      // Use filter prop directly to ensure correct value on navigation
      const activeFilter = filter || connectionTypeFilter;
      if (activeFilter) {
        // FIX 2026-05-19: menu Statik/DHCP uses URL filter='dhcp' but DB enum stores 'static'.
        const _f = activeFilter.toLowerCase();
        const dbValue = _f === 'dhcp' ? 'static' : _f;
        params.append('connection_type', dbValue);
      }
      
      // Add online/offline filter
      if (onlineFilter && onlineFilter !== 'all') {
        params.append('is_online', onlineFilter === 'online' ? '1' : '0');
      }
      if (isolirFilter && isolirFilter !== 'all') {
        params.append('is_isolated', isolirFilter === 'isolated' ? '1' : '0');
      }
      if (weakSignalFilter) {
        params.append('weak_signal', '1');
      }
      if (paymentStatusFilter && paymentStatusFilter !== 'all') {
        params.append('payment_status', paymentStatusFilter);
      }
      if (registrationPeriodFilter) {
        params.append('registration_period', registrationPeriodFilter);
      }
      if (gracePeriodFilter) {
        params.append('grace_period', '1');
      }
      if (waInactiveFilter) {
        params.append('wa_inactive', '1');
      }
      
      // Use API v2 (MySQL)
      const response = await axios.get(`${API_URL}/api/v2/customers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      
      if (response.data.success) {
        // Map MySQL response to frontend format
        const mappedCustomers = (response.data.data || []).map(mapCustomerFromMySQL);
        setCustomers(mappedCustomers);
        
        // Calculate pagination from total
        const total = response.data.total || 0;
        setTotalCount(total);
        setTotalPages(Math.ceil(total / itemsPerPage));
      }
    } catch (error) {
      // 2026-08-08: silent ignore aborted request
      if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching customers:', error);
      toast.error('Gagal memuat data pelanggan');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch bandwidth ONLY for customers with MikroTik user configured

  // Fetch ACS devices status
  const fetchAcsDevicesStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/acs/devices/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAcsDevicesStatus(response.data.data || {});
      }
    } catch (error) {
      console.error('Error fetching ACS status:', error);
    }
  };

  // Fetch ACS status when customers are loaded
  useEffect(() => {
    if (customers.length > 0) {
      fetchAcsDevicesStatus();
    }
  }, [customers]);

  const fetchAllCustomersBandwidth = async (customerList) => {
    const bandwidthMap = {};
    
    // Filter: Only fetch for customers that have mikrotik_user configured
    const customersWithMikrotik = customerList.filter(
      customer => customer.mikrotik_user && customer.mikrotik_user.trim() !== ''
    );
    
    // Skip if no customers have MikroTik configured
    if (customersWithMikrotik.length === 0) {
      setCustomersBandwidth({});
      return;
    }
    
    // Fetch bandwidth only for customers with MikroTik user
    const promises = customersWithMikrotik.map(async (customer) => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/api/mikrotik/bandwidth/${customer.id}`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000 // 5 second timeout to prevent long waits
          }
        );
        
        if (response.data.success && response.data.data && response.data.data.online) {
          // API returns bytes_in (download) and bytes_out (upload)
          bandwidthMap[customer.id] = {
            upload: response.data.data.bytes_out || 0,
            download: response.data.data.bytes_in || 0,
            uploadReadable: response.data.data.bytes_out_readable || '-',
            downloadReadable: response.data.data.bytes_in_readable || '-',
            online: response.data.data.online
          };
        } else {
          bandwidthMap[customer.id] = null;
        }
      } catch (error) {
        // Silent fail for individual customers
        bandwidthMap[customer.id] = null;
      }
    });

    await Promise.allSettled(promises);
    setCustomersBandwidth(bandwidthMap);
  };

  // Format bandwidth to human readable format
  const formatBandwidth = (bytes, readable = null) => {
    // Use pre-formatted readable string if available
    if (readable && readable !== '-') {
      return readable;
    }
    
    if (!bytes || bytes === 0) return '-';
    
    // Convert bytes to readable format
    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;
    
    if (gb >= 1) {
      return `${gb.toFixed(2)} GB`;
    } else if (mb >= 1) {
      return `${mb.toFixed(2)} MB`;
    } else if (kb >= 1) {
      return `${kb.toFixed(2)} KB`;
    } else {
      return `${bytes} B`;
    }
  };


  // Validate form before submit
  const validateForm = () => {
    const errors = [];
    
    // Required fields validation
    if (!safeTrim(formData.name)) {
      errors.push('Nama Lengkap harus diisi');
    }
    
    if (!safeTrim(formData.email)) {
      errors.push('Email harus diisi');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Format Email tidak valid');
    }
    
    if (!safeTrim(formData.phone)) {
      errors.push('No. Telepon harus diisi');
    }
    
    if (!safeTrim(formData.address)) {
      errors.push('Alamat harus diisi');
    }
    
    // NIK validation - OPTIONAL, but if provided must be 16 digits
    if (safeTrim(formData.nik) !== '' && !/^\d{16}$/.test(formData.nik)) {
      const currentLength = formData.nik.length;
      errors.push(`NIK harus berisi 16 digit angka (saat ini ${currentLength} digit)`);
    }
    
    if (!formData.branch_id || formData.branch_id === '') {
      errors.push('Cabang harus dipilih (Tab: Info Pelanggan)');
    }
    
    // Owner is MANDATORY
    if (!formData.owner || formData.owner === '') {
      errors.push('Owner harus dipilih (Tab: Info Pelanggan)');
    }
    
    // Tab 2: Paket Langganan - Paket Layanan is MANDATORY
    if (!formData.service_plan || formData.service_plan === '') {
      errors.push('Paket Layanan harus dipilih (Tab: Paket Langganan)');
    }
    
    // Other fields have defaults, but still validate if changed to empty
    if (!formData.connection_type || formData.connection_type === '') {
      errors.push('Tipe Koneksi harus dipilih (Tab: Paket Langganan)');
    }
    
    // Tab 2: Paket Langganan - MANDATORY FIELDS (continued)
    // Biaya 0 diizinkan untuk paket FREE (mis. Free Hotspot member). Hanya tolak nilai negatif atau null/undefined.
    if (formData.monthly_fee == null || formData.monthly_fee === '' || Number(formData.monthly_fee) < 0) {
      errors.push('Biaya Bulanan tidak boleh kosong/negatif (Tab: Paket Langganan)');
    }
    
    if (!formData.installation_date || formData.installation_date === '') {
      errors.push('Tanggal Pemasangan harus diisi (Tab: Paket Langganan)');
    }
    
    // PPPoE credentials validation - MANDATORY for PPPoE connection type
    const connectionType = (formData.connection_type || '').toLowerCase();
    if (connectionType === 'pppoe') {
      if (connectionType === 'static') {
        if (!formData.static_ip || String(formData.static_ip).trim() === '') {
          errors.push('IP Address Statik wajib diisi untuk tipe koneksi Static (Tab: Info Billing)');
        }
      } else {
        if (!formData.mikrotik_user || String(formData.mikrotik_user).trim() === '') {
          errors.push('Username PPPoE wajib diisi untuk tipe koneksi PPPoE (Tab: Paket Langganan)');
        }
        if (!formData.mikrotik_password || String(formData.mikrotik_password).trim() === '') {
          errors.push('Password PPPoE wajib diisi untuk tipe koneksi PPPoE (Tab: Paket Langganan)');
        }
      }
    }
    
    // Longitude and Latitude validation - Optional, but if filled must be valid
    const lonRaw = formData.longitude;
    const lonStr = (lonRaw === null || lonRaw === undefined) ? '' : String(lonRaw).trim();
    if (lonStr !== '') {
      const lon = parseFloat(lonStr);
      if (isNaN(lon) || lon < -180 || lon > 180) {
        errors.push('Longitude harus berupa angka antara -180 dan 180');
      }
    }

    const latRaw = formData.latitude;
    const latStr = (latRaw === null || latRaw === undefined) ? '' : String(latRaw).trim();
    if (latStr !== '') {
      const lat = parseFloat(latStr);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        errors.push('Latitude harus berupa angka antara -90 dan 90');
      }
    }
    
    // Delta #78: username PPPoE/Hotspot terpakai -> blokir simpan
    if (formData._username_taken) {
      errors.push(`Username '${formData.mikrotik_user}' sudah dipakai — pilih username lain (Tab: Paket Langganan)`);
    }
    // Tab 4: Perangkat - Merk MANDATORY (2026-07-05)
    // Merk Perangkat: optional (2026-07-07)
    
    setValidationErrors(errors);
    
    // Show validation errors as toast notifications
    if (errors.length > 0) {
      console.log('Validation failed with errors:', errors);
      // Show all errors up to a reasonable count so user knows exactly which fields to fix.
      if (errors.length === 1) {
        toast.error(`Wajib diisi: ${errors[0]}`);
      } else if (errors.length <= 5) {
        errors.forEach(err => toast.error(err));
      } else {
        toast.error(`${errors.length} field wajib belum diisi. Lihat daftar di bawah form (warna merah).`);
      }
    }
    
    return errors.length === 0;
  };


  // Export Customers state + handler — backend handles branch scoping for non-superadmin
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStatusFilter, setExportStatusFilter] = useState('all');
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportCustomers = async (format) => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('token');
      // Match connection_type to active URL filter (/customers/hotspot, /customers/pppoe, /customers/statik)
      const _connMap = { hotspot: 'hotspot', pppoe: 'pppoe', statik: 'static', dhcp: 'static' };
      const _connType = _connMap[filter] || '';
      const _connQS = _connType ? `&connection_type=${_connType}` : '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/v2/export-customers?format=${format}&status_filter=${exportStatusFilter}${_connQS}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      let ext = 'json';
      if (format === 'csv') ext = 'csv';
      if (format === 'excel') ext = 'xlsx';
      const ts = new Date().toISOString().slice(0,19).replace(/[-:T]/g,'');
      const _typeSfx = _connType ? `_${_connType}` : '';
      const suffix = (exportStatusFilter && exportStatusFilter !== 'all' ? `_${exportStatusFilter}` : '') + _typeSfx;
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `pelanggan${suffix}_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Export pelanggan (${format.toUpperCase()}) berhasil diunduh`);
      setExportDialogOpen(false);
    } catch (err) {
      console.error('Export pelanggan failed:', err);
      toast.error('Gagal export pelanggan');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submit
    if (!validateForm()) {
      toast.error('Mohon lengkapi semua field yang wajib diisi');
      // Scroll validation banner into view
      setTimeout(() => {
        try {
          const el = document.querySelector('[data-testid="validation-errors-banner"]');
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (_e) {}
      }, 80);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Debug: Log formData before mapping
      console.log('Form payment_status:', formData.payment_status);
      
      // FIX 2026-10-06 (Delta #40): default billing day = hari instalasi,
      // konsisten dengan tampilan select di CustomerFormTabs.
      const _submitDefaultBillingDay = (() => {
        try {
          const _iso = String(formData.installation_date || new Date().toISOString().slice(0, 10));
          const _d = parseInt(_iso.slice(8, 10), 10);
          if (!_d || _d < 1) return 1;
          return Math.min(_d, 28);
        } catch (_e) { return 1; }
      })();

      // Map frontend formData to MySQL schema
      const mysqlData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        nik: formData.nik,
        // FIX 2026-05-19: send null (not '') for non-PPPoE customers to avoid UNIQUE 'pppoe_username' duplicate-empty error
        pppoe_username: (formData.connection_type || '').toLowerCase() === 'static' ? null : (formData.mikrotik_user || null),
        pppoe_password: (formData.connection_type || '').toLowerCase() === 'static' ? null : (formData.mikrotik_password || null),
        // Static IP fields -> mapped to current_ip / current_mac in DB
        current_ip: (formData.connection_type || '').toLowerCase() === 'static' ? (formData.static_ip || null) : null,
        current_mac: (formData.connection_type || '').toLowerCase() === 'static' ? (formData.static_mac || null) : null,
        static_gateway: formData.static_gateway || null,
        static_interface: formData.static_interface || null,
        service_plan_name: formData.service_plan,
        monthly_price: formData.monthly_fee || formData.price || 0,
        billing_date: formData.billing_date || _submitDefaultBillingDay,
        due_date: formData.due_date || formData.billing_date || _submitDefaultBillingDay,
        payment_status: formData.payment_status,  // Use exact value, no fallback
        is_active: formData.is_active !== false,
        connection_type: (formData.connection_type || 'pppoe').toLowerCase(),
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        owner_id: formData.owner_id ? String(formData.owner_id) : null,
        owner_name: formData.owner,
        branch_id: formData.branch_id ? String(formData.branch_id) : null,
        branch_name: formData.branch_name,
        tenant_id: formData.tenant_id ? String(formData.tenant_id) : null,
        // PPN fields
        bill_ppn: formData.bill_ppn || false,
        tax_percentage: formData.bill_ppn ? (formData.tax_percentage || 11) : 0,
        // Prorate fields
        prorate_otomatis: formData.auto_prorate || false,
        prorate_day: formData.auto_prorate ? (parseInt(formData.prorate_date) || 0) : 0,
        // Promo fields
        aktifkan_promo: formData.activate_promo || false,
        promo_percentage: formData.activate_promo ? (formData.promo_percentage || 0) : 0,
        promo_duration_months: formData.activate_promo ? (formData.promo_duration_months || 0) : 0,
        promo_start_date: formData.activate_promo ? (formData.installation_date || new Date().toISOString().split('T')[0]) : null,
        promo_months_used: 0,
        // Billing extra fields
        biaya_instalasi: formData.installation_fee || 0,
        fee_seller: formData.seller_fee || 0,
        sewa_perangkat: formData.device_rental || 0,
        biaya_lain_lain: formData.other_fees || 0,
        // Info Pelanggan
        portal_password: formData.portal_password || '12345678',
        registration_status: formData.registration_status === 'aktif' ? 'aktif' : 'on_process',
        customer_type: formData.customer_type || 'reguler',
        payment_type: formData.payment_type || 'prepaid',
        account_status: formData.account_status || 'enabled',
        bind_on_login: formData.bind_on_login || false,
        notes: formData.note || null,
        installation_date: formData.installation_date || null,
        // Perangkat pelanggan (2026-07-05)
        device_brand: formData.device_brand || null,
        device_model: formData.device_model || null,
        device_sn: (formData.device_sn || '').trim() || null,
        device_band: formData.device_band || null,
        device_photo_url: formData.device_photo_url || null
      };
      
      let savedCustomerId = editingCustomerId;
      if (isEditMode) {
        // Update existing customer - use API v2
        await axios.put(`${API_URL}/api/v2/customers/${editingCustomerId}`, mysqlData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerUpdated'));
      } else {
        // Create new customer - use API v2
        const createRes = await axios.post(`${API_URL}/api/v2/customers`, mysqlData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedCustomerId = createRes?.data?.data?.id || createRes?.data?.id || createRes?.data?.customer_id || null;
        toast.success(t('customerAdded'));
      }

      // Sync ODP link (idempotent): unlinks old, links new, auto-assigns port
      if (savedCustomerId) {
        try {
          const odpIdRaw = formData.odp_id;
          const odpId = odpIdRaw === '' || odpIdRaw === null || odpIdRaw === undefined ? null : parseInt(odpIdRaw);
          await axios.post(`${API_URL}/api/odp-pop/set-customer-link`,
            { customer_id: parseInt(savedCustomerId), odp_id: odpId },
            { headers: { Authorization: `Bearer ${token}` } });
        } catch (odpErr) {
          console.error('Failed to sync ODP link:', odpErr);
          toast.error('Pelanggan tersimpan, namun gagal menghubungkan ke ODP');
        }
      }
      
      setIsDialogOpen(false);
      setIsEditMode(false);
      setEditingCustomerId(null);
      fetchCustomers();
      setFormData({
        account_number: '',
        name: '',
        email: '',
        phone: '',
        address: '',
        nik: '',
        longitude: '',
        latitude: '',
        ip_address: 'Automatic',
        owner: '',
        branch_id: '',
        note: '',
        service_plan: '',
        connection_type: 'PPPoE',
        registration_status: 'aktif',
        customer_type: 'reguler',
        payment_type: 'prepaid',
        payment_status: 'sudah_bayar',
        account_status: 'enabled',
        bind_on_login: false,
        monthly_fee: 0,
        installation_date: new Date().toISOString().split('T')[0],
        bill_ppn: false,
        tax_percentage: 0,
        auto_prorate: false,
        prorate_date: null,
        prorate_days_remaining: 0,
        activate_promo: false,
        promo_percentage: 0,
        promo_duration_months: 0,
        final_invoice_price: 0,
        installation_fee: 0,
        seller_fee: 0,
        device_rental: 0,
        mikrotik_user: '',
        mikrotik_password: '',
        create_mikrotik_user: false,
        // Perangkat pelanggan (2026-07-05)
        device_brand: '',
        device_model: '',
        device_sn: '',
        device_band: '',
        device_photo_url: ''
      });
      setValidationErrors([]);
    } catch (error) {
      console.error('Error saving customer:', error);
      
      // Handle backend validation errors (400 or 422)
      if (error.response && (error.response.status === 400 || error.response.status === 422)) {
        const errorData = error.response.data;
        
        // Pydantic validation error format
        if (errorData.detail && Array.isArray(errorData.detail)) {
          // Extract error messages from Pydantic validation errors
          const errorMessages = errorData.detail.map(err => {
            const field = err.loc ? err.loc.join(' -> ') : 'Field';
            return `${field}: ${err.msg}`;
          });
          setValidationErrors(errorMessages);
          toast.error('Validasi gagal: ' + errorMessages[0]);
        } else if (errorData.detail && typeof errorData.detail === 'string') {
          toast.error(errorData.detail);
          setValidationErrors([errorData.detail]);
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          // Backend returned validation errors array
          setValidationErrors(errorData.errors);
          toast.error(errorData.message || 'Validasi gagal di server');
        } else if (typeof errorData === 'string') {
          toast.error(errorData);
        } else {
          toast.error('Gagal menyimpan data. Mohon periksa kembali.');
        }
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.detail || t('error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (customer) => {
    setIsEditMode(true);
    setEditingCustomerId(customer.id);
    setFormData({
      id: customer.id,
      account_number: customer.account_number || '',
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      nik: customer.nik || '',
      longitude: customer.longitude || '',
      latitude: customer.latitude || '',
      ip_address: customer.ip_address || 'Automatic',
      owner: customer.owner || '',
      branch_id: customer.branch_id || '',
      note: customer.note || '',
      password: customer.password || '12345678', // Password Portal
      service_plan: customer.service_plan || '',
      connection_type: customer.connection_type || 'PPPoE',
      registration_status: customer.registration_status || 'aktif',
      customer_type: customer.customer_type || 'reguler',
      payment_type: customer.payment_type || 'prepaid',
      billing_date: customer.billing_date || 1,
      due_date: customer.due_date || 10,
      payment_status: customer.payment_status || 'sudah_bayar',
      account_status: customer.account_status || 'enabled',
      bind_on_login: customer.bind_on_login || false,
      monthly_fee: customer.monthly_price || customer.monthly_fee || 0,
      installation_date: customer.installation_date ? new Date(customer.installation_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      bill_ppn: customer.bill_ppn || false,
      tax_percentage: customer.tax_percentage || 0,
      // Map database fields to frontend fields
      auto_prorate: customer.prorate_otomatis || false,
      prorate_date: customer.prorate_day || null,
      prorate_days_remaining: customer.prorate_day ? (() => {
        const t = new Date();
        const activation = t.getDate();
        const due = parseInt(customer.prorate_day);
        const lastDay = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
        let d = activation <= due ? (due - activation) : ((lastDay - activation) + due);
        return Math.max(0, Math.min(30, d));
      })() : 0,
      activate_promo: customer.aktifkan_promo || false,
      promo_percentage: customer.promo_percentage || 0,
      promo_duration_months: customer.promo_duration_months || 0,
      final_invoice_price: customer.final_invoice_price || 0,
      installation_fee: customer.biaya_instalasi || 0,
      seller_fee: customer.fee_seller || 0,
      device_rental: customer.sewa_perangkat || 0,
      other_fees: customer.biaya_lain_lain || 0,
      mikrotik_user: customer.pppoe_username || customer.mikrotik_user || '',
      mikrotik_password: customer.pppoe_password || customer.mikrotik_password || '',
      static_ip: customer.current_ip || '',
      static_mac: customer.current_mac || '',
      static_gateway: customer.static_gateway || '',
      static_interface: customer.static_interface || '',
      create_mikrotik_user: false,
      // Perangkat pelanggan (2026-07-05)
      device_brand: customer.device_brand || '',
      device_model: customer.device_model || '',
      device_sn: customer.device_sn || '',
      device_band: customer.device_band || '',
      device_photo_url: customer.device_photo_url || ''
    });
    setIsDialogOpen(true);
  };

  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  const [showSingleDeleteDialog, setShowSingleDeleteDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteCustomerPreview, setDeleteCustomerPreview] = useState(null);
  const [deleteCustomerConfirmText, setDeleteCustomerConfirmText] = useState('');
  const [loadingDeletePreview, setLoadingDeletePreview] = useState(false);

  const handleDelete = async (id) => {
    setDeleteCustomerId(id);
    setShowSingleDeleteDialog(true);
    setDeleteCustomerPreview(null);
    setDeleteCustomerConfirmText('');
    setLoadingDeletePreview(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/v2/customers/${id}/delete-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) setDeleteCustomerPreview(res.data.data);
    } catch (e) {
      console.error('cust preview err', e);
    } finally {
      setLoadingDeletePreview(false);
    }
  };

  const confirmSingleDelete = async () => {
    if (!deleteCustomerId) return;
    try {
      const token = localStorage.getItem('token');
      // Default: soft delete (move to trash, reversible within 30 days)
      const url = `${API_URL}/api/v2/customers/${deleteCustomerId}`;
      const res = await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.soft_delete) {
        toast.success('✅ Pelanggan dipindahkan ke Tempat Sampah');
      } else {
        toast.success(t('customerDeleted'));
      }
      setShowSingleDeleteDialog(false);
      setDeleteCustomerId(null);
      setDeleteCustomerPreview(null);
      setDeleteCustomerConfirmText('');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
      console.error(error);
    }
  };

  // Handle reset password
  const handleResetPassword = (customer) => {
    setSelectedCustomerForReset(customer);
    setNewPassword('');
    setShowResetPasswordDialog(true);
  };

  const confirmResetPassword = async () => {
    if (!selectedCustomerForReset) return;
    
    if (!newPassword) {
      toast.error('Password tidak boleh kosong');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    setResetPasswordLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/v2/customers/${selectedCustomerForReset.id}/reset-password`,
        null,
        { 
          params: { new_password: newPassword },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`Password untuk ${selectedCustomerForReset.name} berhasil direset`);
      setShowResetPasswordDialog(false);
      setSelectedCustomerForReset(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reset password');
      console.error(error);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Toggle payment status (Lunas/Belum Bayar)
  const handleTogglePaymentStatus = async (customer) => {
    // Check based on displayed/computed status
    const currentStatus = customer.payment_status_computed || 'BELUM LUNAS';
    
    if (currentStatus === 'LUNAS') {
      // If already LUNAS, just toggle back to BELUM BAYAR without dialog
      const loadingToast = toast.loading(`Mengubah status pembayaran ${customer.name}...`);
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/api/v2/customers/${customer.id}`,
          { payment_status: 'belum_bayar' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.dismiss(loadingToast);
        toast.success(`Status pembayaran ${customer.name} diubah ke Belum Bayar`);
        setTimeout(async () => {
          await fetchCustomers();
        }, 300);
      } catch (error) {
        toast.dismiss(loadingToast);
        toast.error(error.response?.data?.detail || 'Gagal mengubah status pembayaran');
        console.error(error);
      }
    } else {
      // If BELUM LUNAS, show dialog with date options
      setLunasCustomer(customer);
      setLunasDateOption('due_date'); // Default to due date
      setLunasPaymentDate(format(new Date(), 'yyyy-MM-dd')); // Default to today
      setShowLunasDialog(true);
    }
  };

  // Record Payment: Always open Lunas dialog regardless of current payment status.
  // Use case: customer whose due_date is today/near and is currently LUNAS wants to pay
  // for the next billing cycle. Opens the same dialog as Ubah ke Lunas — extending next_due_date forward.
  const handleRecordPayment = (customer) => {
    setLunasCustomer(customer);
    setLunasDateOption('due_date');
    setLunasPaymentDate(format(new Date(), 'yyyy-MM-dd'));
    setShowLunasDialog(true);
  };

  // Handle confirm Lunas with date option
  const handleConfirmLunas = async () => {
    if (!lunasCustomer) return;
    
    setLunasLoading(true);
    try {
      const token = localStorage.getItem('token');
      const today = format(new Date(), 'yyyy-MM-dd');
      
      // Prepare request data
      // paid_date is ALWAYS today (when payment is made)
      // calculation_base determines how next_due_date is calculated
      const requestData = {
        payment_status: 'sudah_bayar',
        paid_date: today, // Always today - when payment was made
      };
      
      console.log('DEBUG Lunas: lunasDateOption =', lunasDateOption);
      console.log('DEBUG Lunas: lunasCustomer.next_due_date =', lunasCustomer.next_due_date);
      
      if (lunasDateOption === 'due_date') {
        // SESUAI TANGGAL JATUH TEMPO: same day next month
        requestData.calculation_base = 'due_date';
        requestData.base_date = lunasCustomer.next_due_date || lunasCustomer.due_date;
        console.log('DEBUG: Using due_date, base_date =', requestData.base_date);
      } else {
        // SESUAI TANGGAL BAYAR: same day next month from payment date
        requestData.calculation_base = 'payment_date';
        requestData.base_date = lunasPaymentDate;
        console.log('DEBUG: Using payment_date, base_date =', requestData.base_date);
      }
      
      console.log('DEBUG: Full requestData =', JSON.stringify(requestData));
      
      await axios.put(
        `${API_URL}/api/v2/customers/${lunasCustomer.id}`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Status pembayaran ${lunasCustomer.name} diubah ke Lunas (${lunasDateOption === 'due_date' ? 'sesuai jatuh tempo' : 'sesuai tanggal bayar'})`);
      setShowLunasDialog(false);
      setLunasCustomer(null);
      
      setTimeout(async () => {
        await fetchCustomers();
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah status pembayaran');
      console.error(error);
    } finally {
      setLunasLoading(false);
    }
  };


  // 2026-07-30: Debt-mode handlers
  const handleMarkDebt = (customer) => {
    setDebtCustomer(customer);
    // Default deadline = (customer.next_due_date OR today) + 30 days
    try {
      const base = customer.next_due_date ? new Date(customer.next_due_date) : new Date();
      base.setDate(base.getDate() + 30);
      setDebtDeadline(format(base, 'yyyy-MM-dd'));
    } catch {
      setDebtDeadline('');
    }
    setDebtNote('');
    setDebtModalOpen(true);
  };

  const handleConfirmMarkDebt = async () => {
    if (!debtCustomer) return;
    if (!debtDeadline) { toast.error('Pilih tanggal deadline'); return; }
    setDebtLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/v2/customers/${debtCustomer.id}/mark-debt`,
        { deadline_date: debtDeadline, note: debtNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${debtCustomer.name} masuk masa tenggang s/d ${debtDeadline}`);
      setDebtModalOpen(false);
      setDebtCustomer(null);
      setTimeout(() => fetchCustomers(), 300);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menandai berhutang');
    } finally {
      setDebtLoading(false);
    }
  };

  const handleClearDebt = async (customer) => {
    if (!customer) return;
    if (!window.confirm(`Hapus masa tenggang untuk ${customer.name}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/v2/customers/${customer.id}/clear-debt`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Masa tenggang ${customer.name} dihapus`);
      setTimeout(() => fetchCustomers(), 300);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal hapus tanda hutang');
    }
  };

  // Handle checkbox selection
  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  // 2026-06-12: Bulk Tandai Lunas from floating action bar
  const handleConfirmBulkPaid = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Pilih minimal 1 pelanggan terlebih dulu.');
      return;
    }
    setBulkPaidLoading(true);
    setBulkPaidResult(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/v2/customers/bulk-mark-paid`,
        {
          customer_ids: selectedCustomers,
          calculation_base: bulkPaidForm.calculationBase,
          payment_date: bulkPaidForm.paymentDate,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const sum = resp.data?.summary || {};
      setBulkPaidResult(resp.data);
      const msg = `${sum.paid || 0} pelanggan di-Lunaskan, ${sum.already_paid || 0} skip (sudah lunas), ${sum.failed || 0} gagal. ${sum.journals_created_or_backfilled || 0} jurnal akuntansi dibuat.`;
      if ((sum.failed || 0) > 0) {
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
      setSelectedCustomers([]);
      fetchCustomers && fetchCustomers();
    } catch (err) {
      console.error('bulk-mark-paid customers failed', err);
      toast.error(err.response?.data?.detail || 'Gagal Tandai Lunas pelanggan terpilih');
    } finally {
      setBulkPaidLoading(false);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectedCustomers.length === currentCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(currentCustomers.map(customer => customer.id));
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      // Default: soft delete (move to trash, reversible)
      const body = { customer_ids: selectedCustomers };
      const response = await axios.post(`${API_URL}/api/v2/customers/bulk-delete`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.soft_delete) {
        toast.success(`✅ ${response.data.soft_count} pelanggan dipindahkan ke Tempat Sampah`);
      } else {
        toast.success(response.data.message);
      }
      setSelectedCustomers([]);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      setBulkDeletePreview(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus pelanggan');
    }
  };

  // Open delete confirmation modal
  const openDeleteConfirm = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Pilih minimal 1 pelanggan untuk dihapus');
      return;
    }
    setShowDeleteConfirm(true);
    setBulkDeletePreview(null);
    setDeleteConfirmText('');
    setLoadingBulkPreview(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/v2/customers/bulk-delete-preview`,
        { customer_ids: selectedCustomers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) setBulkDeletePreview(res.data.data);
    } catch (e) {
      console.error('bulk preview err', e);
    } finally {
      setLoadingBulkPreview(false);
    }
  };

  // Close delete confirmation modal
  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setBulkDeletePreview(null);
  };

  // Handle bulk toggle active
  const handleBulkToggleActive = async (isActive) => {
    if (selectedCustomers.length === 0) {
      toast.error('Pilih minimal 1 pelanggan');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/customers/bulk-toggle-active`,
        { customer_ids: selectedCustomers, is_active: isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setSelectedCustomers([]);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah status pelanggan');
    }
  };

  // Handle single customer toggle active
  const handleToggleActive = async (customerId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/v2/customers/${customerId}/toggle-active`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah status');
    }
  };


  // MikroTik Operations
  const handleDisableCustomer = async (customerId) => {
    setMikrotikLoading(prev => ({...prev, [customerId]: true}));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/disable/${customerId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Customer berhasil di-disable');
        fetchCustomers();
      } else {
        toast.error(response.data.message || 'Gagal disable customer');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal disable customer');
    } finally {
      setMikrotikLoading(prev => ({...prev, [customerId]: false}));
    }
  };

  const handleEnableCustomer = async (customerId) => {
    setMikrotikLoading(prev => ({...prev, [customerId]: true}));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/enable/${customerId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Customer berhasil di-enable');
        fetchCustomers();
      } else {
        toast.error(response.data.message || 'Gagal enable customer');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal enable customer');
    } finally {
      setMikrotikLoading(prev => ({...prev, [customerId]: false}));
    }
  };

  const checkCustomerOnlineStatus = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/mikrotik/status/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomerOnlineStatus(prev => ({
          ...prev,
          [customerId]: response.data.online || false
        }));
      }
    } catch (error) {
      console.log('Failed to check online status:', error);
    }
  };

  const handleViewBandwidth = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/mikrotik/bandwidth/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const data = response.data.data;
        toast.success(
          `Bandwidth Usage:\nDownload: ${data.bytes_in_readable}\nUpload: ${data.bytes_out_readable}`,
          { duration: 5000 }
        );
      } else {
        toast.error(response.data.message || 'Gagal mendapatkan data bandwidth');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mendapatkan bandwidth usage');
    }
  };


  const handleIsolirCustomer = async (customerId) => {
    if (!window.confirm('Yakin ingin isolir customer ini? Internet akan diblokir.')) {
      return;
    }
    
    setMikrotikLoading(prev => ({...prev, [customerId]: true}));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/isolir/${customerId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Customer berhasil di-isolir');
        fetchCustomers();
        // 2026-02-13: kick+reconnect butuh ~5-10s sebelum MikroTik push
        // IP pool isolir yang baru. Refresh lagi supaya UI dapat IP terbaru.
        setTimeout(() => { fetchCustomers(); }, 6000);
        setTimeout(() => { fetchCustomers(); }, 15000);
      } else {
        toast.error(response.data.message || 'Gagal isolir customer');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal isolir customer');
    } finally {
      setMikrotikLoading(prev => ({...prev, [customerId]: false}));
    }
  };

  const handleUnisolirCustomer = async (customerId) => {
    setMikrotikLoading(prev => ({...prev, [customerId]: true}));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/unisolir/${customerId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Customer berhasil di-unisolir');
        fetchCustomers();
        // 2026-02-13: kick+reconnect butuh ~5-10s sebelum MikroTik push
        // IP normal yang baru. Refresh lagi supaya UI dapat IP terbaru.
        setTimeout(() => { fetchCustomers(); }, 6000);
        setTimeout(() => { fetchCustomers(); }, 15000);
      } else {
        toast.error(response.data.message || 'Gagal unisolir customer');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal unisolir customer');
    } finally {
      setMikrotikLoading(prev => ({...prev, [customerId]: false}));
    }
  };

  // Remote ONT States
  const [remoteLoading, setRemoteLoading] = useState({});
  
  // Remote PPPoE States
  const [remotePPPoELoading, setRemotePPPoELoading] = useState({});
  
  // Copy to clipboard function with fallback
  const copyToClipboard = async (text, label = 'Text') => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      toast.success(`${label} "${text}" disalin ke clipboard`);
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error(`Gagal menyalin: ${text}`);
    }
  };
  
  // Link WAN Device States
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingCustomer, setLinkingCustomer] = useState(null);
  const [unlinkedDevices, setUnlinkedDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [fetchingDevices, setFetchingDevices] = useState(false);
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const [suggestedDevice, setSuggestedDevice] = useState(null);
  // Auto-link ONT state
  const [autoLinkLoading, setAutoLinkLoading] = useState(false);

  // Fetch unlinked WAN devices
  const fetchUnlinkedDevices = async (pppoeUsername = '', searchQuery = '') => {
    setFetchingDevices(true);
    try {
      const token = localStorage.getItem('token');
      const params = searchQuery ? { search: searchQuery } : (pppoeUsername ? { search: pppoeUsername } : {});
      const response = await axios.get(`${API_URL}/api/wan-devices/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });
      if (response.data.success) {
        const devices = response.data.data || [];
        setUnlinkedDevices(devices);
        
        // Auto-suggest device matching customer's pppoe username
        if (pppoeUsername) {
          const match = devices.find(d => 
            d.pppoe_username?.toLowerCase() === pppoeUsername.toLowerCase()
          );
          if (match) {
            setSuggestedDevice(match);
            setSelectedDeviceId(match.device_id);
            toast.success(`Device cocok ditemukan: ${match.serial_number}`, { duration: 3000 });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching unlinked devices:', error);
      toast.error('Gagal mengambil daftar WAN devices');
    }
    setFetchingDevices(false);
  };

  // Open link modal for a customer
  const openLinkModal = (customer) => {
    setLinkingCustomer(customer);
    setSelectedDeviceId('');
    setSuggestedDevice(null);
    setDeviceSearchQuery('');
    setShowLinkModal(true);
    // Pass customer's pppoe username to auto-suggest
    const pppoeUser = customer.mikrotik_user || customer.pppoe_username || '';
    setDeviceSearchQuery(pppoeUser); // Pre-fill search with customer's pppoe
    fetchUnlinkedDevices(pppoeUser);
  };

  // Filter devices based on search query
  const filteredDevices = useMemo(() => {
    if (!deviceSearchQuery.trim()) return unlinkedDevices.slice(0, 50); // Show first 50 if no search
    
    const query = deviceSearchQuery.toLowerCase().trim();
    return unlinkedDevices.filter(d => 
      d.serial_number?.toLowerCase().includes(query) ||
      d.pppoe_username?.toLowerCase().includes(query) ||
      d.tr069_ip?.includes(query) ||
      d.external_ip?.includes(query) ||
      d.model?.toLowerCase().includes(query)
    ).slice(0, 50); // Limit to 50 results
  }, [unlinkedDevices, deviceSearchQuery]);

  // Handle link WAN device to customer
  // Auto-link all ONTs
  const handleAutoLinkAll = async () => {
    setAutoLinkLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/v2/customers/auto-link-ont`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.linked_count > 0) {
          fetchCustomers();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal auto-link ONT");
    } finally {
      setAutoLinkLoading(false);
    }
  };

  const handleLinkDevice = async () => {
    if (!selectedDeviceId || !linkingCustomer) {
      toast.error('Pilih WAN device terlebih dahulu');
      return;
    }

    setLinkLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/customers/${linkingCustomer.id}/link-wan-device`,
        { device_id: selectedDeviceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'WAN device berhasil di-link');
        setShowLinkModal(false);
        setLinkingCustomer(null);
        setSelectedDeviceId('');
        setSuggestedDevice(null);
        // Refresh customer list
        fetchCustomers();
      } else {
        toast.error(response.data.message || 'Gagal link WAN device');
      }
    } catch (error) {
      console.error('Error linking device:', error);
      toast.error(error.response?.data?.detail || 'Gagal link WAN device');
    }
    setLinkLoading(false);
  };

  // Handle Enable WAN Web via TR-069 (Huawei/ZTE ONT)
  const [enableWanWebLoading, setEnableWanWebLoading] = useState({});
  const handleEnableWanWeb = async (customer) => {
    if (!customer?.pppoe_username && !customer?.last_ip_address) {
      toast.error('Butuh pppoe_username atau IP untuk enable WAN web');
      return;
    }
    setEnableWanWebLoading(prev => ({ ...prev, [customer.id]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/v2/remote-ont/enable-wan-web`, {
        pppoe_username: customer.pppoe_username,
        ont_ip: customer.last_ip_address || undefined
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        const vendor = response.data.data?.vendor || 'unknown';
        toast.success(`Push TR-069 sukses (vendor: ${vendor}). Tunggu 1-2 menit lalu coba Remote ONT.`, { duration: 8000 });
      } else {
        toast.error(response.data.message || 'Enable WAN Web gagal', { duration: 8000 });
      }
    } catch (error) {
      console.error('Error enable WAN web:', error);
      toast.error(error.response?.data?.message || error.response?.data?.detail || 'Gagal push TR-069', { duration: 8000 });
    } finally {
      setEnableWanWebLoading(prev => ({ ...prev, [customer.id]: false }));
    }
  };
  // Handle Remote ONT Access - Using Port-Based Proxy (like old billing system)
  // Tries TR069 IP first, then falls back to External IP (PPPoE IP)
  const handleRemoteONT = async (tr069Ip, externalIp, serialNumber, customerId) => {
    const ipToTry = tr069Ip || externalIp;
    
    if (!ipToTry) {
      toast.error('IP ONT tidak tersedia');
      return;
    }
    
    // Set loading for this specific customer
    setRemoteLoading(prev => ({ ...prev, [customerId]: true }));
    
    const tryCreateProxy = async (deviceIp, isRetry = false) => {
      const token = localStorage.getItem('token');
      
      try {
        const response = await axios.post(`${API_URL}/api/port-proxy/create`, {
          device_ip: deviceIp,
          device_port: 80
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success && response.data.data.url) {
          return { success: true, data: response.data.data, ip: deviceIp };
        }
        return { success: false, error: response.data.data?.message };
      } catch (error) {
        return { success: false, error: error.response?.data?.detail || error.message };
      }
    };
    
    try {
      // First try TR069 IP
      let result = null;
      if (tr069Ip) {
        result = await tryCreateProxy(tr069Ip);
      }
      
      // If TR069 fails and we have external IP, try that
      if ((!result || !result.success) && externalIp && externalIp !== tr069Ip) {
        toast.info('TR069 IP tidak tersedia, mencoba IP PPPoE...', { duration: 2000 });
        result = await tryCreateProxy(externalIp, true);
      }
      
      if (result && result.success) {
        const remoteUrl = result.data.url;
        const port = result.data.port;
        
        // Open ONT web UI in new tab immediately
        window.open(remoteUrl, '_blank');
        toast.success(`Remote ONT ${serialNumber || 'device'} via ${result.ip} - Port ${port}`, { duration: 3000 });
      } else {
        toast.error(result?.error || 'Gagal membuat port proxy. Pastikan L2TP tunnel aktif.');
      }
    } catch (error) {
      console.error('Error creating remote access:', error);
      toast.error('Gagal membuat akses remote. Pastikan L2TP tunnel aktif.');
    } finally {
      setRemoteLoading(prev => ({ ...prev, [customerId]: false }));
    }
  };

  // Handle Remote PPPoE Access - Create port proxy to customer's PPPoE IP
  const handleRemotePPPoE = async (customer) => {
    const pppoeIp = customer.last_ip_address;
    const customerId = customer.id;
    
    if (!pppoeIp && !customer.pppoe_username) {
      toast.error('PPPoE username tidak tersedia untuk customer ini.');
      return;
    }
    
    if (!customer.is_online) {
      toast.error('Customer sedang offline. Remote PPPoE hanya tersedia saat customer online.');
      return;
    }
    
    // Set loading
    setRemotePPPoELoading(prev => ({ ...prev, [customerId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      
      // Use V2 MySQL endpoint (socat_l2tp mode - server-side proxy over L2TP tunnel)
      const response = await axios.post(`${API_URL}/api/v2/remote-ont/mikrotik/create`, {
        ont_ip: pppoeIp || undefined,
        pppoe_username: customer.pppoe_username || undefined,
        ont_port: 80,
        customer_id: customerId,
        duration_minutes: 60
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data?.public_url) {
        const remoteUrl = response.data.data.public_url;
        const port = response.data.data.public_port;
        
        // Open in new tab
        window.open(remoteUrl, '_blank');
        toast.success(`Remote ONT ${customer.name} via ${pppoeIp} - Port ${port}`, { duration: 4000 });
      } else {
        // Backend error message (e.g., ONT tidak reachable) prefers root-level "message"
        toast.error(response.data.message || response.data.data?.message || 'Gagal membuat remote ONT proxy', { duration: 8000 });
      }
    } catch (error) {
      console.error('Error creating remote ONT:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Gagal membuat akses remote ONT. Pastikan L2TP tunnel MikroTik ke NOC aktif.';
      toast.error(errorMsg, { duration: 8000 });
    } finally {
      setRemotePPPoELoading(prev => ({ ...prev, [customerId]: false }));
    }
  };

  // Handle View ONT Bandwidth
  const handleViewONTBandwidth = async (customer) => {
    setBandwidthLoading(true);
    setShowBandwidthDialog(true);
    setBandwidthData(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/mikrotik/bandwidth/${customer.id}`);
      if (response.data.success) {
        const data = response.data.data;
        setBandwidthData({
          customerName: customer.name,
          accountNumber: customer.account_number,
          username: data.username || customer.mikrotik_user,
          bytesIn: data.bytes_in_readable || '0 B',
          bytesOut: data.bytes_out_readable || '0 B',
          packetsIn: data.packets_in || 0,
          packetsOut: data.packets_out || 0,
          online: data.online || customer.is_online
        });
      } else {
        toast.error(response.data.message || 'Gagal mendapatkan data bandwidth');
        setShowBandwidthDialog(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mendapatkan bandwidth usage');
      setShowBandwidthDialog(false);
    } finally {
      setBandwidthLoading(false);
    }
  };


  // STOPPED_TAB_V1 (Delta #79): tab Pelanggan Berhenti
  const _roleKey = (_userRoleLower || '').replace(/\s+/g, '_');
  const _showListTabs = _roleKey !== 'teknisi';
  const _stoppedConnType = filter === 'hotspot' ? 'hotspot' : (filter === 'dhcp' ? 'static' : (filter === 'pppoe' ? 'pppoe' : ''));
  const _changeListTab = (t) => {
    setListTab(t);
    try { sessionStorage.setItem('customers_list_tab', t); } catch (_e) { /* non-fatal */ }
  };
  const _listTabsEl = _showListTabs ? <CustomerListTabs active={listTab} onChange={_changeListTab} connectionType={_stoppedConnType} /> : null;
  if (_showListTabs && listTab === 'stopped') {
    return (
      <div className="space-y-4 animate-fade-in p-3 sm:p-6 bg-gradient-to-br from-slate-50 to-indigo-50/30 dark:from-slate-950 dark:to-slate-900 min-h-screen">
        {_listTabsEl}
        <StoppedCustomersPanel connectionType={_stoppedConnType} canStop={['super_admin', 'superadmin', 'admin', 'kepala_cabang'].includes(_roleKey)} onOpenCustomer={handleViewDetail} />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in p-3 sm:p-6 bg-gradient-to-br from-slate-50 to-indigo-50/30 dark:from-slate-950 dark:to-slate-900 min-h-screen">
      {_listTabsEl}
      {/* IP Conflict warning banner */}
      {ipConflictCount.ips > 0 && (
        <div
          className="flex items-center justify-between gap-3 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 cursor-pointer hover:from-amber-100 hover:to-orange-100 transition-colors"
          onClick={() => navigate('/app/customers/ip-conflicts')}
          data-testid="ip-conflict-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 dark:bg-amber-900/40">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                Ditemukan {ipConflictCount.ips} IP duplikat ({ipConflictCount.customers} pelanggan terdampak)
              </p>
              <p className="text-xs text-amber-700 mt-0.5 dark:text-amber-300">
                2+ pelanggan berbagi IP yang sama — tidak dapat online bersamaan. Klik untuk lihat detail & perbaiki.
              </p>
            </div>
          </div>
          <ExternalLink className="w-4 h-4 text-amber-600 flex-shrink-0 dark:text-amber-400" />
        </div>
      )}

      {/* Header Actions */}
      <Card className="border-0 shadow-xl bg-white/90 dark:bg-slate-900/80 backdrop-blur-sm ring-1 ring-slate-200/50 dark:ring-slate-700/50">
        <CardContent className="pt-4 pb-4">
          {/* Search and Filters — REGISTERED_CARD_UI_V1: flex-col-reverse so "Manajemen Pelanggan" row appears above counters */}
          <div className="flex flex-col gap-3" data-layout="THREE_ROW_LAYOUT_V1">
            {/* THREE_ROW_LAYOUT_V1: Manajemen Pelanggan alone on top */}
            <div className="flex flex-wrap gap-2 items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="customers-menu-aksi"
                    className="h-8 sm:h-10 px-3 text-[10px] sm:text-sm font-semibold tracking-wide bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm hover:shadow-md transition-all gap-2 rounded-lg"
                    title="Manajemen Pelanggan"
                  >
                    <Menu className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="inline">Manajemen Pelanggan</span>
                    <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Filter Status</div>
                  <DropdownMenuItem onClick={() => { setOnlineFilter('all'); setIsolirFilter('all'); setWeakSignalFilter(false); setWaInactiveFilter(false); setPaymentStatusFilter('all'); setCurrentPage(1); }}>
                    <span className="w-4 h-4 mr-2 rounded-full bg-blue-100 inline-flex items-center justify-center text-blue-600 text-[10px] font-bold dark:bg-blue-900/40 dark:text-blue-400">A</span>
                    Semua
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setOnlineFilter('online'); setCurrentPage(1); }}>
                    <Wifi className="w-4 h-4 mr-2 text-emerald-600 dark:text-emerald-400" />
                    Online
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setOnlineFilter('offline'); setCurrentPage(1); }}>
                    <WifiOff className="w-4 h-4 mr-2 text-slate-500 dark:text-slate-400" />
                    Offline
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setIsolirFilter(isolirFilter === 'isolated' ? 'all' : 'isolated'); setCurrentPage(1); }}>
                    <Ban className="w-4 h-4 mr-2 text-red-600 dark:text-red-400" />
                    Isolir
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setIsolirFilter(isolirFilter === 'active' ? 'all' : 'active'); setCurrentPage(1); }}>
                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-600 dark:text-emerald-400" />
                    Aktif
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setWeakSignalFilter(prev => !prev); setCurrentPage(1); }}>
                    <Signal className="w-4 h-4 mr-2 text-amber-600 dark:text-amber-400" />
                    Sinyal Lemah
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setWaInactiveFilter(prev => !prev); setCurrentPage(1); }} data-testid="filter-wa-inactive">
                    <MessageCircleOff className="w-4 h-4 mr-2 text-rose-600 dark:text-rose-400" />
                    WA Tidak Aktif
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setPaymentStatusFilter(paymentStatusFilter === 'lunas' ? 'all' : 'lunas'); setCurrentPage(1); }} data-testid="filter-payment-lunas">
                    <span className="w-4 h-4 mr-2 rounded-full bg-emerald-100 inline-flex items-center justify-center text-emerald-600 text-[10px] font-bold dark:bg-emerald-900/40 dark:text-emerald-400">L</span>
                    Lunas
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setPaymentStatusFilter(paymentStatusFilter === 'belum_lunas' ? 'all' : 'belum_lunas'); setCurrentPage(1); }} data-testid="filter-payment-belum-lunas">
                    <span className="w-4 h-4 mr-2 rounded-full bg-orange-100 inline-flex items-center justify-center text-orange-600 text-[10px] font-bold dark:bg-orange-900/40 dark:text-orange-400">B</span>
                    Belum Lunas
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* Owner Filter (submenu) - moved from top bar */}
                  {!isOwnerRole && (
                    <>
                      <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Filter Pemilik</div>
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="pl-2">
                          <UserCircle className="w-4 h-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                          <span className="flex-1 truncate">{ownerFilter === 'all' ? 'Semua Pemilik' : (owners.find(o => o.id.toString() === ownerFilter.toString())?.name || 'Semua Pemilik')}</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                          <DropdownMenuItem onClick={() => { setOwnerFilter('all'); setCurrentPage(1); }} className={ownerFilter === 'all' ? 'bg-indigo-100 dark:bg-indigo-900/30' : ''}>
                            Semua Pemilik
                          </DropdownMenuItem>
                          {owners.map((owner) => (
                            <DropdownMenuItem key={owner.id} onClick={() => { setOwnerFilter(owner.id); setCurrentPage(1); }} className={ownerFilter.toString() === owner.id.toString() ? 'bg-indigo-100 dark:bg-indigo-900/30' : ''}>
                              {owner.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  {/* Payment Type Filter - moved from top bar */}
                  <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Tipe Pembayaran</div>
                  <DropdownMenuItem onClick={() => { setPaymentTypeFilter('all'); setCurrentPage(1); }} className={paymentTypeFilter === 'all' ? 'bg-indigo-100 dark:bg-indigo-900/30' : ''}>
                    <span className="w-4 h-4 mr-2 rounded-full bg-indigo-100 inline-flex items-center justify-center text-indigo-600 text-[10px] font-bold dark:bg-indigo-900/40 dark:text-indigo-400">A</span>
                    Semua Tipe
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setPaymentTypeFilter('prepaid'); setCurrentPage(1); }} className={paymentTypeFilter === 'prepaid' ? 'bg-emerald-100 dark:bg-emerald-900/30' : ''}>
                    <span className="w-4 h-4 mr-2 rounded-full bg-emerald-100 inline-flex items-center justify-center text-emerald-600 text-[10px] font-bold dark:bg-emerald-900/40 dark:text-emerald-400">P</span>
                    Prepaid (PRE)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setPaymentTypeFilter('postpaid'); setCurrentPage(1); }} className={paymentTypeFilter === 'postpaid' ? 'bg-blue-100 dark:bg-blue-900/30' : ''}>
                    <span className="w-4 h-4 mr-2 rounded-full bg-blue-100 inline-flex items-center justify-center text-blue-600 text-[10px] font-bold dark:bg-blue-900/40 dark:text-blue-400">B</span>
                    Postpaid (POST)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Aksi</div>
                  <DropdownMenuItem onClick={() => { fetchCustomers(); fetchAcsDevicesStatus(); toast.success('Data sedang direfresh...'); }}>
                    <RefreshCw className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                    Refresh
                  </DropdownMenuItem>
                  {_userRoleLower !== 'teknisi' && (
                    <DropdownMenuItem onClick={() => { setIsEditMode(false); resetForm(); setIsDialogOpen(true); }}>
                      <UserPlus className="w-4 h-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      Tambah Pelanggan
                    </DropdownMenuItem>
                  )}
                  {!['teknisi'].includes(_userRoleLower) && (
                    <DropdownMenuItem onClick={() => setExportDialogOpen(true)}>
                      <Download className="w-4 h-4 mr-2 text-emerald-600 dark:text-emerald-400" />
                      Export Pelanggan
                    </DropdownMenuItem>
                  )}
                  {['super_admin','admin','kepala_cabang'].includes(_userRoleLower) && (
                    <DropdownMenuItem onClick={() => navigate(`/app/customers/bulk-import?type=${filter === 'hotspot' ? 'hotspot' : 'pppoe'}`)} data-testid="dropdown-import-excel">
                      <Upload className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                      Import dari Excel
                    </DropdownMenuItem>
                  )}
                  {['super_admin','admin'].includes(_userRoleLower) && (
                    <DropdownMenuItem onClick={() => setScanIsolirOpen(true)} data-testid="dropdown-scan-isolir">
                      <ShieldOff className="w-4 h-4 mr-2 text-rose-600 dark:text-rose-400" />
                      Cek & Isolir Akun
                    </DropdownMenuItem>
                  )}
                  {filter === 'dhcp' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setScanArpOpen(true)}>
                        <Search className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                        Scan IP Router
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setImportQueueOpen(true)}>
                        <Upload className="w-4 h-4 mr-2 text-emerald-600 dark:text-emerald-400" />
                        Import dari Queue
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="sm:hidden" />
                  <DropdownMenuItem className="sm:hidden" onClick={toggleDensity}>
                    <Activity className="w-4 h-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                    Mode: {density === 'compact' ? 'Compact' : 'Nyaman'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* STAT_CARDS_ROW_V1 — 4 large stat cards replacing pill counters */}
            <div className={'grid grid-cols-2 gap-3 mb-3 ' + (_userRoleLower === 'teknisi' ? 'md:grid-cols-3' : 'md:grid-cols-4')}>
              {/* Card 1: Registrasi Bulan Ini */}
              <button
                type="button"
                onClick={() => { setRegistrationPeriodFilter(registrationPeriodFilter === 'month' ? null : 'month'); setCurrentPage(1); }}
                data-testid="stat-card-registrasi"
                title="Klik untuk filter tabel pelanggan yang registrasi bulan ini"
                className={'flex items-center gap-3 p-3 rounded-lg border transition-all text-left hover:shadow-md ' + (registrationPeriodFilter === 'month' ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-400')}
              >
                <div className={'flex-shrink-0 w-11 h-11 rounded-md flex items-center justify-center ' + (registrationPeriodFilter === 'month' ? 'bg-blue-500/30 text-white' : 'bg-blue-500 text-white')}>
                  <UserPlus className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={'text-[10px] uppercase tracking-wide font-semibold ' + (registrationPeriodFilter === 'month' ? 'text-blue-50' : 'text-slate-500 dark:text-slate-400')}>REGISTRASI BULAN INI</div>
                  <div className={'text-2xl font-bold leading-tight ' + (registrationPeriodFilter === 'month' ? 'text-white' : 'text-slate-800 dark:text-slate-100')}>{onlineCounts.loading ? '...' : (onlineCounts.registered_this_month || 0).toLocaleString('id-ID')}</div>
                </div>
              </button>

              {/* Card 2: Online — clickable filter (hidden for teknisi) */}
              {_userRoleLower !== 'teknisi' && (
              <button
                type="button"
                onClick={() => { setOnlineFilter(onlineFilter === 'online' ? 'all' : 'online'); setCurrentPage(1); }}
                data-testid="stat-card-online"
                title="Klik untuk filter pelanggan yang sedang Online"
                className={'flex items-center gap-3 p-3 rounded-lg border transition-all text-left hover:shadow-md ' + (onlineFilter === 'online' ? 'bg-emerald-600 text-white border-emerald-700 shadow-md' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-emerald-400')}
              >
                <div className={'flex-shrink-0 w-11 h-11 rounded-md flex items-center justify-center ' + (onlineFilter === 'online' ? 'bg-emerald-500/40 text-white' : 'bg-emerald-600 text-white')}>
                  <Wifi className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={'text-[10px] uppercase tracking-wide font-semibold ' + (onlineFilter === 'online' ? 'text-emerald-50' : 'text-slate-500 dark:text-slate-400')}>ONLINE</div>
                  <div className={'text-2xl font-bold leading-tight ' + (onlineFilter === 'online' ? 'text-white' : 'text-slate-800 dark:text-slate-100')}>{onlineCounts.loading ? '...' : (onlineCounts.online || 0).toLocaleString('id-ID')}</div>
                </div>
              </button>
              )}

              {/* Card 3: Offline — clickable filter */}
              <button
                type="button"
                onClick={() => { setOnlineFilter(onlineFilter === 'offline' ? 'all' : 'offline'); setCurrentPage(1); }}
                data-testid="stat-card-offline"
                title="Klik untuk filter pelanggan yang sedang Offline"
                className={'flex items-center gap-3 p-3 rounded-lg border transition-all text-left hover:shadow-md ' + (onlineFilter === 'offline' ? 'bg-slate-700 text-white border-slate-800 shadow-md' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-500')}
              >
                <div className={'flex-shrink-0 w-11 h-11 rounded-md flex items-center justify-center ' + (onlineFilter === 'offline' ? 'bg-slate-600/60 text-white' : 'bg-slate-600 text-white')}>
                  <WifiOff className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={'text-[10px] uppercase tracking-wide font-semibold ' + (onlineFilter === 'offline' ? 'text-slate-100' : 'text-slate-500 dark:text-slate-400')}>OFFLINE</div>
                  <div className={'text-2xl font-bold leading-tight ' + (onlineFilter === 'offline' ? 'text-white' : 'text-slate-800 dark:text-slate-100')}>{onlineCounts.loading ? '...' : (onlineCounts.offline || 0).toLocaleString('id-ID')}</div>
                </div>
              </button>
              {/* Card 4: Masa Tenggang (Grace Period) — clickable filter */}
              <button
                type="button"
                onClick={() => { setGracePeriodFilter(!gracePeriodFilter); setCurrentPage(1); }}
                data-testid="stat-card-masa-tenggang"
                title="Klik untuk filter pelanggan yang sedang dalam Masa Tenggang"
                className={'flex items-center gap-3 p-3 rounded-lg border transition-all text-left hover:shadow-md ' + (gracePeriodFilter ? 'bg-rose-600 text-white border-rose-700 shadow-md' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-rose-400')}
              >
                <div className={'flex-shrink-0 w-11 h-11 rounded-md flex items-center justify-center ' + (gracePeriodFilter ? 'bg-rose-500/40 text-white' : 'bg-rose-600 text-white')}>
                  <Clock className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={'text-[10px] uppercase tracking-wide font-semibold ' + (gracePeriodFilter ? 'text-rose-50' : 'text-slate-500 dark:text-slate-400')}>MASA TENGGANG</div>
                  <div className={'text-2xl font-bold leading-tight ' + (gracePeriodFilter ? 'text-white' : 'text-slate-800 dark:text-slate-100')}>{onlineCounts.loading ? '...' : (onlineCounts.grace_period_count || 0).toLocaleString('id-ID')}</div>
                </div>
              </button>

            </div>

            {/* Row: Filters and Actions (search moved next to Tampilkan entri) */}
            <div className="flex flex-wrap gap-2 items-center">
              {/* Consolidated Menu Aksi dropdown */}
              {/* 2026-06-28: Quick-access "Tambah Pelanggan" button (visible to all non-teknisi roles).
                  Item ini tetap ada di dropdown Manajemen Pelanggan sebagai fallback. */}
              {_userRoleLower !== 'teknisi' && (
                <Button
                  data-testid="customers-add-quick-btn"
                  onClick={() => { setIsEditMode(false); resetForm(); setIsDialogOpen(true); }}
                  className="h-8 sm:h-10 px-3 sm:px-4 text-[10px] sm:text-sm font-semibold tracking-wide text-white shadow-md hover:shadow-lg transition-all gap-2 rounded-lg border-0 hover:brightness-110"
                  style={(() => {
                    const bgSolid = branding?.header_color || '#2563eb';
                    const bgStart = branding?.header_gradient_start || bgSolid;
                    const bgEnd = branding?.header_gradient_end || bgSolid;
                    // Compute contrast text (black on light bg, white on dark bg)
                    const hexToRgb = (h) => {
                      const s = (h || '').replace('#','');
                      if (s.length !== 6) return null;
                      const n = parseInt(s, 16);
                      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
                    };
                    const luminance = (h) => {
                      const c = hexToRgb(h);
                      if (!c) return 1;
                      return (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
                    };
                    // Sample midpoint of gradient
                    const sampleColor = branding?.header_use_gradient ? bgStart : bgSolid;
                    const lum = luminance(sampleColor);
                    const contrastColor = lum > 0.6 ? '#0f172a' : '#ffffff';
                    return {
                      background: branding?.header_use_gradient
                        ? `linear-gradient(90deg, ${bgStart} 0%, ${bgEnd} 100%)`
                        : bgSolid,
                      color: contrastColor,
                      '--btn-contrast-text': contrastColor
                    };
                  })()}
                  title="Tambah Pelanggan Baru"
                >
                  <UserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="inline">Tambah Pelanggan</span>
                </Button>
              )}

              {/* Active Filter Badge — replaces all status filter buttons (now in Menu) */}
              {(onlineFilter !== 'all' || isolirFilter !== 'all' || weakSignalFilter || waInactiveFilter) && (
                <div className="flex items-center gap-1 flex-wrap">
                  {onlineFilter === 'online' && <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 gap-1 dark:bg-emerald-900/40 dark:text-emerald-300"><Wifi className="w-3 h-3" />Online <button onClick={() => { setOnlineFilter('all'); setCurrentPage(1); }} className="ml-1 hover:bg-emerald-200 rounded-full"><X className="w-3 h-3" /></button></Badge>}
                  {onlineFilter === 'offline' && <Badge className="bg-slate-200 text-slate-700 border border-slate-300 gap-1 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600"><WifiOff className="w-3 h-3" />Offline <button onClick={() => { setOnlineFilter('all'); setCurrentPage(1); }} className="ml-1 hover:bg-slate-300 rounded-full"><X className="w-3 h-3" /></button></Badge>}
                  {isolirFilter === 'isolated' && <Badge className="bg-red-100 text-red-700 border border-red-300 gap-1 dark:bg-red-900/40 dark:text-red-300"><Ban className="w-3 h-3" />Isolir <button onClick={() => { setIsolirFilter('all'); setCurrentPage(1); }} className="ml-1 hover:bg-red-200 rounded-full"><X className="w-3 h-3" /></button></Badge>}
                  {isolirFilter === 'active' && <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 gap-1 dark:bg-emerald-900/40 dark:text-emerald-300"><CheckCircle className="w-3 h-3" />Aktif <button onClick={() => { setIsolirFilter('all'); setCurrentPage(1); }} className="ml-1 hover:bg-emerald-200 rounded-full"><X className="w-3 h-3" /></button></Badge>}
                  {weakSignalFilter && <Badge className="bg-amber-100 text-amber-700 border border-amber-300 gap-1 dark:bg-amber-900/40 dark:text-amber-300"><Signal className="w-3 h-3" />Sinyal Lemah <button onClick={() => { setWeakSignalFilter(false); setCurrentPage(1); }} className="ml-1 hover:bg-amber-200 rounded-full"><X className="w-3 h-3" /></button></Badge>}
                  {waInactiveFilter && <Badge data-testid="active-filter-wa-inactive" className="bg-rose-100 text-rose-700 border border-rose-300 gap-1 dark:bg-rose-900/40 dark:text-rose-300"><MessageCircleOff className="w-3 h-3" />WA Tidak Aktif <button onClick={() => { setWaInactiveFilter(false); setCurrentPage(1); }} className="ml-1 hover:bg-rose-200 rounded-full" data-testid="badge-wa-inactive-remove"><X className="w-3 h-3" /></button></Badge>}
                  {paymentStatusFilter === 'lunas' && <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 gap-1 dark:bg-emerald-900/40 dark:text-emerald-300"><CheckCircle className="w-3 h-3" />Lunas <button onClick={() => { setPaymentStatusFilter('all'); setCurrentPage(1); }} className="ml-1 hover:bg-emerald-200 rounded-full" data-testid="badge-lunas-remove"><X className="w-3 h-3" /></button></Badge>}
                  {paymentStatusFilter === 'belum_lunas' && <Badge className="bg-orange-100 text-orange-700 border border-orange-300 gap-1 dark:bg-orange-900/40 dark:text-orange-300"><Ban className="w-3 h-3" />Belum Lunas <button onClick={() => { setPaymentStatusFilter('all'); setCurrentPage(1); }} className="ml-1 hover:bg-orange-200 rounded-full" data-testid="badge-belum-lunas-remove"><X className="w-3 h-3" /></button></Badge>}
                </div>
              )}

              {/* Bulk Delete Button - Show when customers selected (hidden for role=teknisi) */}
              {selectedCustomers.length > 0 && _userRoleLower !== 'teknisi' && (
                <Button 
                  variant="destructive"
                  className="h-8 sm:h-10 px-2 sm:px-3 text-[10px] sm:text-sm bg-red-600 hover:bg-red-700"
                  onClick={openDeleteConfirm}
                >
                  <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Hapus</span>
                  <span className="ml-1">({selectedCustomers.length})</span>
                </Button>
              )}



              {/* Cek & Isolir Akun Dialog (2026-09-06) */}
              <ScanIsolirDialog
                open={scanIsolirOpen}
                onOpenChange={setScanIsolirOpen}
                onEditCustomer={async (customerId) => {
                  // 2026-02: Click customer row in scan-isolir modal -> open Edit Dialog
                  const fromList = customers.find((c) => Number(c.id) === Number(customerId));
                  if (fromList) { handleEdit(fromList); return; }
                  try {
                    const token = localStorage.getItem('token');
                    const r = await axios.get(`${API_URL}/api/v2/customers/${customerId}`, { headers: { Authorization: 'Bearer ' + token } });
                    if (r.data) handleEdit(r.data.data || r.data);
                  } catch (e) {
                    toast.error('Gagal load customer: ' + (e.response?.data?.detail || e.message));
                  }
                }}
              />

              {/* Export Pelanggan Dialog */}
              <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Download className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      Export Pelanggan
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div>
                      <label className="text-sm font-semibold text-slate-700 mb-2 block dark:text-slate-200">
                        Filter Status Pelanggan
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { val: 'all', label: 'Semua Pelanggan', color: 'slate' },
                          { val: 'active', label: 'Aktif Saja', color: 'emerald' },
                          { val: 'unpaid', label: 'Belum Bayar', color: 'amber' },
                          { val: 'paid', label: 'Sudah Bayar', color: 'blue' },
                          { val: 'online', label: 'Sedang Online', color: 'green' },
                          { val: 'offline', label: 'Offline (Aktif)', color: 'gray' },
                          { val: 'isolated', label: 'Terisolir', color: 'red' },
                          { val: 'inactive', label: 'Non-Aktif', color: 'zinc' },
                        ].map((opt) => (
                          <button
                            key={opt.val}
                            type="button"
                            data-testid={`export-filter-${opt.val}`}
                            onClick={() => setExportStatusFilter(opt.val)}
                            className={`text-xs py-2 px-3 rounded-md border-2 transition-all ${
                              exportStatusFilter === opt.val
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold shadow-sm'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-700 mb-2 block dark:text-slate-200">
                        Pilih Format File
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          data-testid="export-format-excel"
                          disabled={exportLoading}
                          onClick={() => handleExportCustomers('excel')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        >
                          <FileText className="w-4 h-4 mr-1" /> Excel
                        </Button>
                        <Button
                          data-testid="export-format-csv"
                          disabled={exportLoading}
                          onClick={() => handleExportCustomers('csv')}
                          variant="outline"
                          className="border-blue-500 text-blue-700 hover:bg-blue-50 text-xs dark:text-blue-300 dark:hover:bg-blue-900/30"
                        >
                          <FileText className="w-4 h-4 mr-1" /> CSV
                        </Button>
                        <Button
                          data-testid="export-format-json"
                          disabled={exportLoading}
                          onClick={() => handleExportCustomers('json')}
                          variant="outline"
                          className="border-amber-500 text-amber-700 hover:bg-amber-50 text-xs dark:text-amber-300 dark:hover:bg-amber-900/30"
                        >
                          <FileText className="w-4 h-4 mr-1" /> JSON
                        </Button>
                      </div>
                      {exportLoading && (
                        <p className="text-xs text-slate-500 mt-2 flex items-center gap-1 dark:text-slate-400">
                          <RefreshCw className="w-3 h-3 animate-spin" /> Memproses export...
                        </p>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
<DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                  <DialogHeader>
                    <DialogTitle>{isEditMode ? t('edit') + ' ' + t('customer') : t('addNewCustomer')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="mt-4">
                    <CustomerFormTabs 
                      formData={formData} 
                      setFormData={setFormData} 
                      isEditMode={isEditMode}
                      servicePlans={servicePlans}
                      branches={branches}
                      owners={owners}
                      isOwnerRole={isOwnerRole}
                      currentUser={user}
                      connectionTypeFilter={filter || connectionTypeFilter}
                    />
                  
                  {/* Validation Errors */}
                  {validationErrors.length > 0 && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/30" data-testid="validation-errors-banner">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-800 mb-2">Mohon lengkapi field berikut:</p>
                          <ul className="list-disc list-inside space-y-1">
                            {validationErrors.map((error, index) => (
                              <li key={index} className="text-sm text-red-700 dark:text-red-300">{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-2 pt-6 border-t mt-6">
                    <Button type="button" variant="outline" onClick={() => {
                      setIsDialogOpen(false);
                      setValidationErrors([]);
                    }} disabled={isSubmitting}>
                      {t('cancel')}
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-primary hover:bg-primary/90"
                      disabled={isSubmitting || !!formData._username_taken}
                      title={formData._username_taken ? 'Username PPPoE/Hotspot sudah dipakai — ganti dulu' : undefined}
                      data-testid="btn-save-customer"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Menyimpan...
                        </>
                      ) : (
                        <>{isEditMode ? t('save') + ' ' + t('customer') : t('save') + ' ' + t('customer')}</>
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className="border-0 shadow-xl ring-1 ring-slate-200/50">
        <CardContent className="p-0">
          {/* Show entries + Search + Total */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-slate-600 dark:text-slate-300">{t('showEntries')}</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-slate-300 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-white"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
                <option value={2000}>2000</option>
              </select>
              <span className="text-sm text-slate-600 dark:text-slate-300">{t('entries')}</span>
              {isLoading && (
                <div className="ml-2 flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
                  <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs">Loading...</span>
                </div>
              )}
            </div>
            {/* Search bar - inline between Tampilkan entri and Menampilkan */}
            <div className="relative flex-1 min-w-0 sm:max-w-[360px] sm:ml-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 w-4 h-4" />
              <Input
                data-testid="search-input"
                placeholder={t('searchCustomers')}
                className="w-full pl-10 bg-white dark:bg-slate-800 border-indigo-200 dark:border-slate-700 text-slate-800 dark:text-white text-sm focus:border-indigo-400 focus:ring-indigo-400 h-9 placeholder:text-slate-400 dark:placeholder:text-slate-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* 2026-07-30: Row flash-highlight animation for "Back to Customers" return */}
          <style>{`
            @keyframes customerRowFlash {
              0%   { background-color: rgba(99, 102, 241, 0.35); box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.55); }
              60%  { background-color: rgba(99, 102, 241, 0.18); box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.30); }
              100% { background-color: transparent; box-shadow: none; }
            }
            .customer-row-flash {
              animation: customerRowFlash 2.4s ease-out 1;
              border-radius: 6px;
            }
          `}</style>
          {/* Mobile Card View (extracted to /components/customers/CustomerMobileCards.jsx) */}
          <ErrorBoundary>
          <CustomerMobileCards
            density={density}
            isLoading={isLoading}
            currentCustomers={currentCustomers}
            acsDevicesStatus={acsDevicesStatus}
            branches={branches}
            selectedCustomers={selectedCustomers}
            handleSelectCustomer={handleSelectCustomer}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleResetPassword={handleResetPassword}
            handleTogglePaymentStatus={handleTogglePaymentStatus}
            handleRecordPayment={handleRecordPayment}
            handleToggleActive={handleToggleActive}
            openLinkModal={openLinkModal}
            navigate={navigate}
            onViewDetail={handleViewDetail}
            handleMarkDebt={handleMarkDebt}
            handleClearDebt={handleClearDebt}
            currentUserRole={user?.role}
            handleRemotePPPoE={handleRemotePPPoE}
            remotePPPoELoading={remotePPPoELoading}
            handleEnableWanWeb={handleEnableWanWeb}
            enableWanWebLoading={enableWanWebLoading}
          />

          {/* Desktop Table View (extracted to /components/customers/CustomerDesktopTable.jsx) */}
          <CustomerDesktopTable
            isLoading={isLoading}
            currentCustomers={currentCustomers}
            acsDevicesStatus={acsDevicesStatus}
            selectedCustomers={selectedCustomers}
            sortConfig={sortConfig}
            mikrotikLoading={mikrotikLoading}
            handleSelectCustomer={handleSelectCustomer}
            handleSelectAll={handleSelectAll}
            handleSort={handleSort}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleResetPassword={handleResetPassword}
            handleTogglePaymentStatus={handleTogglePaymentStatus}
            handleRecordPayment={handleRecordPayment}
            handleToggleActive={handleToggleActive}
            handleDisableCustomer={handleDisableCustomer}
            handleEnableCustomer={handleEnableCustomer}
            handleIsolirCustomer={handleIsolirCustomer}
            handleUnisolirCustomer={handleUnisolirCustomer}
            getBranchName={getBranchName}
            navigate={navigate}
            onViewDetail={handleViewDetail}
            handleMarkDebt={handleMarkDebt}
            handleClearDebt={handleClearDebt}
            currentUserRole={user?.role}
            t={t}
            updateCustomerLocal={updateCustomerLocal}
            handleRemotePPPoE={handleRemotePPPoE}
            remotePPPoELoading={remotePPPoELoading}
            handleEnableWanWeb={handleEnableWanWeb}
            enableWanWebLoading={enableWanWebLoading}
          />
          </ErrorBoundary>

          {/* Footer: Showing X-Y of Z (left) + Pagination (right) */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 sm:px-6 py-3 sm:py-4 border-t border-slate-100 dark:border-slate-700">
            <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 order-2 sm:order-1" data-testid="showing-info-footer">
              <span className="hidden sm:inline">{t('showing')} </span>
              {customers.length > 0 ? (
                <>
                  <span className="font-semibold text-indigo-600 dark:text-indigo-400">{((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)}</span>
                  <span className="hidden sm:inline"> {t('of')}</span>
                  <span className="sm:hidden">/</span> <span className="font-semibold">{totalCount.toLocaleString('id-ID')}</span>
                </>
              ) : (
                '0'
              )}
            </div>
            {totalPages > 1 ? (
            <div className="flex flex-wrap items-center justify-end gap-1 sm:gap-2 order-1 sm:order-2 ml-auto">
              {/* Page number indicator (mobile only) */}
              <span className="sm:hidden w-full text-center text-xs text-slate-500 mb-1 dark:text-slate-400">
                Hal {currentPage} / {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                aria-label={t('previous')}
                className="px-2 sm:px-3 py-1 border border-slate-300 rounded-md text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
                data-testid="pagination-prev"
              >
                <span className="sm:hidden">&lsaquo;</span>
                <span className="hidden sm:inline">{t('previous')}</span>
              </button>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                // On mobile, only show current page; on desktop show first/last + neighbors
                const visibleOnMobile =
                  pageNum === 1 || pageNum === totalPages || pageNum === currentPage;
                const visibleOnDesktop =
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                if (visibleOnDesktop) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-2 sm:px-3 py-1 border rounded-md text-xs sm:text-sm min-w-[28px] sm:min-w-[36px] ${
                        currentPage === pageNum
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'border-slate-300 hover:bg-slate-50'
                      } ${visibleOnMobile ? '' : 'hidden sm:inline-block'}`}
                      data-testid={`pagination-page-${pageNum}`}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return (
                    <span
                      key={pageNum}
                      className="hidden sm:inline px-1 sm:px-2 text-xs sm:text-sm text-slate-400 dark:text-slate-500"
                    >
                      …
                    </span>
                  );
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                aria-label={t('next')}
                className="px-2 sm:px-3 py-1 border border-slate-300 rounded-md text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
                data-testid="pagination-next"
              >
                <span className="sm:hidden">&rsaquo;</span>
                <span className="hidden sm:inline">{t('next')}</span>
              </button>
            </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Floating Action Bar - Shows when customers are selected */}
      {selectedCustomers.length > 0 && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-slate-800 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-4 border border-slate-700">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">{selectedCustomers.length} dipilih</span>
          </div>
          <div className="w-px h-6 bg-slate-600"></div>
          {_userRoleLower !== 'teknisi' && (
            <>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                onClick={() => { setBulkPaidResult(null); setBulkPaidDialogOpen(true); }}
                data-testid="customers-bulk-mark-paid-btn"
              >
                <CircleDollarSign className="w-4 h-4 mr-2" />
                Tandai Lunas
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                onClick={openDeleteConfirm}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus Semua
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-300 hover:text-white hover:bg-slate-700"
            onClick={() => setSelectedCustomers([])}
          >
            Batal
          </Button>
        </div>
      )}

      {/* 2026-06-12: Bulk Tandai Lunas Modal */}
      <Dialog open={bulkPaidDialogOpen} onOpenChange={(open) => { setBulkPaidDialogOpen(open); if (!open) setBulkPaidResult(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center gap-2 dark:text-emerald-300">
              <CircleDollarSign className="w-5 h-5" />
              Tandai Lunas ({selectedCustomers.length} pelanggan)
            </DialogTitle>
          </DialogHeader>
          {bulkPaidResult ? (
            <div className="space-y-3 py-3 text-sm">
              <div className="rounded-md border bg-emerald-50 p-3 text-emerald-900 dark:bg-emerald-900/30">
                <div className="font-semibold mb-1">Selesai:</div>
                <div>{bulkPaidResult.message}</div>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300">
                Akun Kas dipakai: {bulkPaidResult.summary?.cash_account?.code} {bulkPaidResult.summary?.cash_account?.name}<br/>
                Akun Pendapatan: {bulkPaidResult.summary?.revenue_account?.code} {bulkPaidResult.summary?.revenue_account?.name}
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-900/30">
                Akan dibuat: payment record, invoice paid, jurnal akuntansi (DR Kas / CR Pendapatan)
                per pelanggan. Idempotent — pelanggan yang sudah lunas akan di-skip
                (kecuali back-fill jurnal kalau payment lama belum ada jurnalnya).
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Tanggal Pelunasan</label>
                <select
                  data-testid="customers-bulk-paid-base"
                  className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm dark:border-slate-600"
                  value={bulkPaidForm.calculationBase}
                  onChange={(e) => setBulkPaidForm((f) => ({ ...f, calculationBase: e.target.value }))}
                >
                  <option value="due_date">Sesuai Tgl Jatuh Tempo masing-masing</option>
                  <option value="payment_date">Tanggal Spesifik (pilih di bawah)</option>
                </select>
              </div>
              {bulkPaidForm.calculationBase === 'payment_date' && (
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Tanggal Bayar</label>
                  <input
                    type="date"
                    data-testid="customers-bulk-paid-date"
                    className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm dark:border-slate-600"
                    value={bulkPaidForm.paymentDate}
                    onChange={(e) => setBulkPaidForm((f) => ({ ...f, paymentDate: e.target.value }))}
                  />
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => { setBulkPaidDialogOpen(false); setBulkPaidResult(null); }}
              disabled={bulkPaidLoading}
            >
              {bulkPaidResult ? 'Tutup' : 'Batal'}
            </Button>
            {!bulkPaidResult && (
              <Button
                data-testid="customers-bulk-paid-confirm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleConfirmBulkPaid}
                disabled={bulkPaidLoading}
              >
                {bulkPaidLoading ? 'Memproses...' : `Konfirmasi Lunaskan ${selectedCustomers.length}`}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => { setShowDeleteConfirm(open); if (!open) { setBulkDeletePreview(null); setDeleteConfirmText(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2 dark:text-red-400">
              <Trash2 className="w-5 h-5" />
              Konfirmasi Penghapusan ({selectedCustomers.length} pelanggan)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {loadingBulkPreview ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-500 border-t-transparent" />
                <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">Memuat data terkait...</span>
              </div>
            ) : bulkDeletePreview ? (
              <>
                {(bulkDeletePreview.invoices.count > 0 || bulkDeletePreview.payments.count > 0
                  || bulkDeletePreview.tickets > 0 || bulkDeletePreview.whatsapp_messages > 0) ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-900/30">
                    <p className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1 dark:text-red-300">
                      <Trash2 className="w-4 h-4" /> Akan dihapus permanen:
                    </p>
                    <div className="space-y-1 text-xs text-red-700 dark:text-red-300" data-testid="bulk-delete-impact">
                      <div className="flex justify-between"><span>Pelanggan:</span><span className="font-bold">{bulkDeletePreview.customers}</span></div>
                      <div className="flex justify-between"><span>Invoice:</span><span className="font-bold">{bulkDeletePreview.invoices.count} (Rp {Number(bulkDeletePreview.invoices.total).toLocaleString('id-ID')})</span></div>
                      <div className="flex justify-between"><span>Pembayaran:</span><span className="font-bold">{bulkDeletePreview.payments.count} (Rp {Number(bulkDeletePreview.payments.total).toLocaleString('id-ID')})</span></div>
                      <div className="flex justify-between"><span>Tiket:</span><span className="font-bold">{bulkDeletePreview.tickets}</span></div>
                      <div className="flex justify-between"><span>Pesan WhatsApp:</span><span className="font-bold">{bulkDeletePreview.whatsapp_messages}</span></div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 dark:bg-amber-900/30">
                    <p className="text-sm text-amber-700 dark:text-amber-300">Tidak ada data transaksi terkait. Aman dihapus.</p>
                  </div>
                )}

                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                  <p className="text-xs text-blue-800 dark:text-blue-300">
                    💡 Pelanggan terpilih akan masuk ke <b>Tempat Sampah</b>. Dapat dipulihkan dalam <b>30 hari</b>.
                  </p>
                </div>
              </>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/30">
                <p className="text-sm text-red-800 font-semibold">⚠️ Tindakan ini tidak dapat dibatalkan!</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeDeleteConfirm}>Batal</Button>
            <Button
              onClick={handleBulkDelete}
              disabled={loadingBulkPreview}
              data-testid="bulk-confirm-btn"
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Pindahkan {selectedCustomers.length} ke Tempat Sampah
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <Dialog open={showSingleDeleteDialog} onOpenChange={(open) => {
        setShowSingleDeleteDialog(open);
        if (!open) { setDeleteCustomerPreview(null); setDeleteCustomerConfirmText(''); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2 dark:text-red-400">
              <Trash2 className="w-5 h-5" />
              Konfirmasi Hapus Pelanggan
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {loadingDeletePreview ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-500 border-t-transparent" />
                <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">Memuat data terkait...</span>
              </div>
            ) : deleteCustomerPreview ? (
              <>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 dark:bg-slate-900 dark:border-slate-700">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">{deleteCustomerPreview.customer.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">No. Akun: <span className="font-mono">{deleteCustomerPreview.customer.account_number}</span></p>
                  {deleteCustomerPreview.customer.pppoe_username && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">PPPoE: <span className="font-mono text-indigo-600 dark:text-indigo-400">{deleteCustomerPreview.customer.pppoe_username}</span></p>
                  )}
                </div>

                {(deleteCustomerPreview.invoices.count > 0 || deleteCustomerPreview.payments.count > 0
                  || deleteCustomerPreview.tickets > 0 || deleteCustomerPreview.whatsapp_messages > 0
                  || deleteCustomerPreview.hardware_unlink > 0 || deleteCustomerPreview.radius_entries) ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-900/30">
                    <p className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1 dark:text-red-300">
                      <Trash2 className="w-4 h-4" /> Akan dihapus permanen:
                    </p>
                    <div className="space-y-1 text-xs text-red-700 dark:text-red-300" data-testid="cust-delete-impact">
                      <div className="flex justify-between"><span>Invoice:</span><span className="font-bold">{deleteCustomerPreview.invoices.count} (Rp {Number(deleteCustomerPreview.invoices.total).toLocaleString('id-ID')})</span></div>
                      <div className="flex justify-between"><span>Pembayaran:</span><span className="font-bold">{deleteCustomerPreview.payments.count} (Rp {Number(deleteCustomerPreview.payments.total).toLocaleString('id-ID')})</span></div>
                      <div className="flex justify-between"><span>Tiket gangguan:</span><span className="font-bold">{deleteCustomerPreview.tickets}</span></div>
                      <div className="flex justify-between"><span>Pesan WhatsApp:</span><span className="font-bold">{deleteCustomerPreview.whatsapp_messages}</span></div>
                      {deleteCustomerPreview.radius_entries && (
                        <div className="flex justify-between"><span>RADIUS entries:</span><span className="font-bold">Ya (radcheck/radreply/radusergroup)</span></div>
                      )}
                      {deleteCustomerPreview.hardware_unlink > 0 && (
                        <div className="flex justify-between text-amber-700 dark:text-amber-300"><span>Perangkat (di-unlink, tidak dihapus):</span><span className="font-bold">{deleteCustomerPreview.hardware_unlink}</span></div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 dark:bg-amber-900/30">
                    <p className="text-sm text-amber-700 dark:text-amber-300">Tidak ada data transaksi terkait. Aman dihapus.</p>
                  </div>
                )}

                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                  <p className="text-xs text-blue-800 dark:text-blue-300">
                    💡 Pelanggan akan masuk ke <b>Tempat Sampah</b>. Anda dapat memulihkannya dalam <b>30 hari</b> sebelum auto-purge permanen.
                  </p>
                </div>
              </>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/30">
                <p className="text-sm text-red-800"><strong>⚠️ Peringatan:</strong> Tindakan ini tidak dapat dibatalkan!</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => {
                setShowSingleDeleteDialog(false);
                setDeleteCustomerId(null);
                setDeleteCustomerPreview(null);
                setDeleteCustomerConfirmText('');
              }}>Batal</Button>
              <Button
                variant="destructive"
                onClick={confirmSingleDelete}
                disabled={loadingDeletePreview}
                data-testid="cust-delete-confirm-btn"
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Pindahkan ke Tempat Sampah
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-amber-600 flex items-center gap-2 dark:text-amber-400">
              <Key className="w-5 h-5" />
              Reset Password Pelanggan
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedCustomerForReset && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 dark:bg-slate-900 dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-300">Pelanggan:</p>
                <p className="font-semibold text-slate-900 dark:text-slate-100">{selectedCustomerForReset.name}</p>
                <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">ID: {selectedCustomerForReset.account_number}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 dark:text-slate-200">
                Password Baru <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Masukkan password baru (min. 6 karakter)"
                className="w-full"
              />
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                Password minimal 6 karakter
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowResetPasswordDialog(false);
                  setSelectedCustomerForReset(null);
                  setNewPassword('');
                }}
                disabled={resetPasswordLoading}
              >
                Batal
              </Button>
              <Button
                onClick={confirmResetPassword}
                disabled={resetPasswordLoading || !newPassword}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {resetPasswordLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bandwidth Dialog */}
      <Dialog open={showBandwidthDialog} onOpenChange={setShowBandwidthDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-600 flex items-center gap-2 dark:text-blue-400">
              <Activity className="w-5 h-5" />
              Bandwidth Usage
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {bandwidthLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : bandwidthData ? (
              <>
                {/* Customer Info */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 dark:bg-slate-900 dark:border-slate-700">
                  <p className="text-sm text-slate-600 dark:text-slate-300">Pelanggan:</p>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">{bandwidthData.customerName}</p>
                  <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">ID: {bandwidthData.accountNumber}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Username: {bandwidthData.username}</p>
                  <div className="mt-2">
                    <Badge className={bandwidthData.online ? "bg-emerald-500" : "bg-slate-400"}>
                      {bandwidthData.online ? "Online" : "Offline"}
                    </Badge>
                  </div>
                </div>

                {/* Bandwidth Stats */}
                <div className="grid grid-cols-1 gap-3">
                  {/* Download */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-600 p-2 rounded-lg">
                        <Download className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-600 dark:text-slate-300">Download</p>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{bandwidthData.bytesIn}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{bandwidthData.packetsIn.toLocaleString()} packets</p>
                      </div>
                    </div>
                  </div>

                  {/* Upload */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 dark:bg-emerald-900/30">
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-600 p-2 rounded-lg">
                        <Upload className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-600 dark:text-slate-300">Upload</p>
                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{bandwidthData.bytesOut}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{bandwidthData.packetsOut.toLocaleString()} packets</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-slate-500 text-center pt-2 dark:text-slate-400">
                  Data dari Mikrotik PPPoE Session
                </div>
              </>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowBandwidthDialog(false)}
              >
                Tutup
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Onboarding Warning Dialog */}
      <Dialog open={showOnboardingWarning} onOpenChange={setShowOnboardingWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Setup Awal Belum Lengkap
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Sebelum menambahkan pelanggan, Anda harus menyelesaikan setup awal berikut:
            </p>

            <div className="space-y-2 bg-slate-50 rounded-lg p-4 dark:bg-slate-900">
              {onboardingProgress && (
                <>
                  <div className={`flex items-center gap-2 ${onboardingProgress.company_profile ? 'text-green-600' : 'text-red-600'}`}>
                    {onboardingProgress.company_profile ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="text-sm font-medium">
                      1. Data Perusahaan {!onboardingProgress.company_profile && '(Belum lengkap)'}
                    </span>
                  </div>

                  <div className={`flex items-center gap-2 ${onboardingProgress.branches ? 'text-green-600' : 'text-red-600'}`}>
                    {onboardingProgress.branches ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="text-sm font-medium">
                      2. Minimal 1 Cabang {!onboardingProgress.branches && '(Belum dibuat)'}
                    </span>
                  </div>

                  <div className={`flex items-center gap-2 ${onboardingProgress.owners ? 'text-green-600' : 'text-red-600'}`}>
                    {onboardingProgress.owners ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="text-sm font-medium">
                      3. Minimal 1 Owner {!onboardingProgress.owners && '(Belum dibuat)'}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
              <p className="text-xs text-blue-800">
                <strong>💡 Petunjuk:</strong> Silakan lengkapi setup di menu Pengaturan → Data Perusahaan, Cabang, dan Owner terlebih dahulu.
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => setShowOnboardingWarning(false)}
              >
                Tutup
              </Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => {
                  setShowOnboardingWarning(false);
                  if (onboardingProgress && !onboardingProgress.company_profile) {
                    navigate('/settings/company');
                  } else if (onboardingProgress && !onboardingProgress.branches) {
                    navigate('/branches');
                  } else if (onboardingProgress && !onboardingProgress.owners) {
                    navigate('/owners');
                  }
                }}
              >
                Pergi ke Setup
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden dark:bg-slate-800">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Upload className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Import Data Pelanggan</h3>
                </div>
                <button 
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportResult(null);
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-5">
              {!importResult ? (
                <>
                  {/* Download Template */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 dark:bg-blue-900/30">
                    <p className="text-sm text-blue-800 mb-2">
                      📋 Download template CSV terlebih dahulu untuk format yang benar:
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tools/import-customers/template`, '_blank');
                      }}
                      className="text-blue-600 border-blue-300 dark:text-blue-400"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template CSV
                    </Button>
                  </div>

                  {/* File Input */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">
                      Pilih File (CSV atau Excel)
                    </label>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600"
                    />
                    {importFile && (
                      <p className="text-xs text-green-600 dark:text-green-400">
                        ✅ File dipilih: {importFile.name}
                      </p>
                    )}
                  </div>

                  <div className="mt-4 text-xs text-gray-500 dark:text-slate-400">
                    <p className="font-medium mb-1">Kolom yang didukung:</p>
                    <p>name, email, phone, address, connection_type, pppoe_username, pppoe_password, package_name, monthly_price, owner, onu_serial, olt_name, is_active</p>
                  </div>
                </>
              ) : (
                /* Import Result */
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${importResult.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <div className="flex items-center gap-3 mb-2">
                      {importResult.imported > 0 ? (
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center dark:bg-green-900/40">
                          <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center dark:bg-yellow-900/40">
                          <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-slate-100">Import Selesai</p>
                        <p className="text-sm text-gray-600 dark:text-slate-300">
                          {importResult.imported} berhasil, {importResult.skipped} dilewati dari {importResult.total_rows} baris
                        </p>
                      </div>
                    </div>
                  </div>

                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-900/30">
                      <p className="text-sm font-medium text-red-800 mb-2">Errors:</p>
                      <ul className="text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto dark:text-red-400">
                        {importResult.errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 bg-gray-50 flex gap-3 dark:bg-slate-900">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="flex-1"
                disabled={importing}
              >
                {importResult ? 'Tutup' : 'Batal'}
              </Button>
              {!importResult && (
                <Button
                  onClick={async () => {
                    if (!importFile) {
                      toast.error('Pilih file terlebih dahulu');
                      return;
                    }
                    
                    setImporting(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', importFile);
                      
                      const token = localStorage.getItem('token');
                      const response = await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/tools/import-customers`,
                        formData,
                        {
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                          }
                        }
                      );
                      
                      if (response.data.success) {
                        setImportResult(response.data.data);
                        if (response.data.data.imported > 0) {
                          toast.success(`${response.data.data.imported} pelanggan berhasil diimport`);
                          fetchCustomers(); // Refresh customer list
                        }
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Gagal import data');
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={!importFile || importing}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Mengimport...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Import Data
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Link WAN Device Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Link WAN Device ke Customer</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {linkingCustomer && (
              <div className="bg-slate-50 p-3 rounded-lg dark:bg-slate-900">
                <p className="text-sm text-slate-600 dark:text-slate-300">Customer:</p>
                <p className="font-medium">{linkingCustomer.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">PPPoE: {linkingCustomer.mikrotik_user || linkingCustomer.pppoe_username || '-'}</p>
              </div>
            )}

            {/* Auto-suggested device */}
            {suggestedDevice && (
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg dark:bg-green-900/30">
                <p className="text-sm font-medium text-green-800 mb-1">✓ Device Cocok Ditemukan!</p>
                <p className="text-sm"><span className="text-green-600 dark:text-green-400">Serial:</span> {suggestedDevice.serial_number}</p>
                <p className="text-sm"><span className="text-green-600 dark:text-green-400">PPPoE:</span> {suggestedDevice.pppoe_username}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2 dark:text-slate-200">
                Cari WAN Device
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <Input
                  value={deviceSearchQuery}
                  onChange={(e) => { setDeviceSearchQuery(e.target.value); if (e.target.value.length >= 3) { fetchUnlinkedDevices("", e.target.value); } else if (e.target.value.length === 0) { fetchUnlinkedDevices(); } }}
                  placeholder="Ketik serial, PPPoE username, atau IP..."
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">
                {unlinkedDevices.length} device ditemukan • Menampilkan {filteredDevices.length} hasil
              </p>
            </div>

            {fetchingDevices ? (
              <div className="flex items-center justify-center py-4">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400 dark:text-slate-500" />
                <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">Memuat daftar device...</span>
              </div>
            ) : filteredDevices.length === 0 ? (
              <div className="text-center py-4 text-slate-500 text-sm dark:text-slate-400">
                <p>Tidak ada device yang cocok</p>
                <p className="text-xs mt-1">Coba kata kunci lain atau pastikan device sudah sync dari ACS</p>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg dark:border-slate-700">
                {filteredDevices.map((device) => (
                  <div
                    key={device.device_id}
                    onClick={() => setSelectedDeviceId(device.device_id)}
                    className={`p-3 cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${
                      selectedDeviceId === device.device_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{device.serial_number}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          PPPoE: <span className="font-medium">{device.pppoe_username || '-'}</span>
                          {device.model && ` • ${device.model}`}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500">
                          TR069: {device.tr069_ip || '-'} • WAN: {device.external_ip || '-'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {device.online ? (
                          <Badge className="bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-medium shadow-sm">Online</Badge>
                        ) : (
                          <Badge className="bg-slate-300 text-slate-600 text-xs font-medium dark:text-slate-300">Offline</Badge>
                        )}
                        {selectedDeviceId === device.device_id && (
                          <CheckCircle className="w-5 h-5 text-blue-500" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkingCustomer(null);
                  setSelectedDeviceId('');
                  setSuggestedDevice(null);
                }}
              >
                Batal
              </Button>
              <Button
                onClick={handleLinkDevice}
                disabled={!selectedDeviceId || linkLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {linkLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Linking...
                  </>
                ) : (
                  <>
                    <Link className="w-4 h-4 mr-2" />
                    Link Device
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2026-07-30: Tandai Berhutang Modal */}
      <Dialog open={debtModalOpen} onOpenChange={(v) => { if (!debtLoading) setDebtModalOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="debt-modal">
          <DialogHeader>
            <DialogTitle>🕐 Tandai Masa Tenggang</DialogTitle>
            <DialogDescription>
              Customer akan dianggap dalam <b>masa tenggang</b> — status belum bayar tapi <b>tidak di-isolir</b> sampai tanggal deadline.
              Jika customer sedang ter-isolir, koneksinya akan <b>otomatis dibuka kembali</b>.
              Setelah deadline lewat tanpa pelunasan → auto-isolir kembali. Jika customer bayar sebelum deadline, tanda masa tenggang otomatis hilang.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {debtCustomer && (
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3 text-sm">
                <div className="font-medium">{debtCustomer.name}</div>
                <div className="text-xs text-slate-500">
                  {debtCustomer.account_number}
                  {debtCustomer.next_due_date ? (' • Jatuh tempo: ' + debtCustomer.next_due_date) : ''}
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="debt-deadline">Deadline Pelunasan *</Label>
              <input
                id="debt-deadline"
                type="date"
                value={debtDeadline}
                onChange={(e) => setDebtDeadline(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm"
                data-testid="debt-deadline-input"
              />
              <p className="text-xs text-slate-500 mt-1">Default: 30 hari setelah jatuh tempo. Bisa diubah.</p>
            </div>
            <div>
              <Label htmlFor="debt-note">Catatan / Alasan (opsional)</Label>
              <Input
                id="debt-note"
                type="text"
                value={debtNote}
                onChange={(e) => setDebtNote(e.target.value.slice(0, 500))}
                placeholder="Misal: minta perpanjangan karena masalah keluarga"
                className="mt-1"
                data-testid="debt-note-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setDebtModalOpen(false)} disabled={debtLoading} data-testid="debt-cancel-btn">
              Batal
            </Button>
            <Button
              onClick={handleConfirmMarkDebt}
              disabled={debtLoading || !debtDeadline}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="debt-confirm-btn"
            >
              {debtLoading ? 'Menyimpan...' : 'Tandai Masa Tenggang'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ubah ke Lunas Dialog */}
      <Dialog open={showLunasDialog} onOpenChange={setShowLunasDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Ubah ke Lunas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {lunasCustomer && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1 dark:bg-slate-900">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{lunasCustomer.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">No. Akun: {lunasCustomer.account_number}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Jatuh Tempo: {lunasCustomer.next_due_date || lunasCustomer.due_date || '-'}
                </p>
              </div>
            )}
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Pilih metode perhitungan jatuh tempo berikutnya:</p>
              
              {/* Option 1: Sesuai Tanggal Jatuh Tempo */}
              <label 
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  lunasDateOption === 'due_date' 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="lunasOption"
                  value="due_date"
                  checked={lunasDateOption === 'due_date'}
                  onChange={(e) => setLunasDateOption(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-slate-800 dark:text-slate-200">Sesuai Tanggal Jatuh Tempo</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Otomatis - untuk pelanggan yang bayar tepat waktu atau lebih awal.
                    Jatuh tempo berikutnya dihitung dari tanggal jatuh tempo saat ini.
                  </p>
                </div>
              </label>
              
              {/* Option 2: Sesuai Tanggal Bayar */}
              <label 
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  lunasDateOption === 'payment_date' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="lunasOption"
                  value="payment_date"
                  checked={lunasDateOption === 'payment_date'}
                  onChange={(e) => setLunasDateOption(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="font-medium text-slate-800 dark:text-slate-200">Sesuai Tanggal Bayar (Manual)</p>
                  <p className="text-xs text-slate-500 mb-2 dark:text-slate-400">
                    Untuk pelanggan yang telat bayar. Jatuh tempo berikutnya dihitung dari tanggal pembayaran.
                  </p>
                  {lunasDateOption === 'payment_date' && (
                    <Input
                      type="date"
                      value={lunasPaymentDate}
                      onChange={(e) => setLunasPaymentDate(e.target.value)}
                      className="mt-2"
                    />
                  )}
                </div>
              </label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowLunasDialog(false)}
              disabled={lunasLoading}
            >
              Batal
            </Button>
            <Button
              onClick={handleConfirmLunas}
              disabled={lunasLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {lunasLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Konfirmasi Lunas
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ScanArpDialog open={scanArpOpen} onClose={() => setScanArpOpen(false)} />
      <ImportFromQueueDialog open={importQueueOpen} onClose={() => setImportQueueOpen(false)} onImported={() => { setImportQueueOpen(false); window.location.reload(); }} />
    </div>
  );
};




// SCAN_ISOLIR_MODAL 2026-09-06 - trigger audit + force isolir customer tunggak
const ScanIsolirDialog = ({ open, onOpenChange, onEditCustomer }) => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const fetchPreview = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API_URL}/api/v2/customer-management/audit-preview`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) setPreview(res.data.preview);
    } catch (e) {
      toast.error(`Gagal load preview: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (open) {
      setPreview(null);
      setResult(null);
      fetchPreview();
    }
  }, [open, fetchPreview]);

  const runScanIsolir = async () => {
    if (!token) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/v2/customer-management/scan-and-isolir`,
        { max_isolir: 500 },
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        setResult(res.data.result);
        toast.success(`Selesai: ${res.data.result.isolir_ok} akun ter-isolir`);
        await fetchPreview();
      }
    } catch (e) {
      toast.error(`Gagal: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const total = preview?.total_potential_action ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="scan-isolir-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-rose-600 dark:text-rose-400" />
            Cek & Isolir Akun Tunggak
          </DialogTitle>
          <DialogDescription className="text-xs">
            Correct payment_status stale + force-isolir customer tunggak yang bypass isolir + clear Framed-IP stray.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400 py-6 flex items-center gap-2 justify-center">
            <RefreshCw className="w-4 h-4 animate-spin" /> Memuat preview...
          </div>
        ) : preview ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 font-semibold">Perlu Correct</div>
                <div className="text-2xl font-bold text-amber-800 dark:text-amber-200" data-testid="preview-correct">{preview.need_correct_payment_status}</div>
                <div className="text-[10px] text-amber-600 dark:text-amber-400">payment_status stale</div>
              </div>
              <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 p-3">
                <div className="text-[10px] uppercase tracking-wider text-rose-700 dark:text-rose-300 font-semibold">Perlu Isolir</div>
                <div className="text-2xl font-bold text-rose-800 dark:text-rose-200" data-testid="preview-isolir">{preview.need_force_isolir}</div>
                <div className="text-[10px] text-rose-600 dark:text-rose-400">tunggak online</div>
              </div>
              <div className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 p-3">
                <div className="text-[10px] uppercase tracking-wider text-indigo-700 dark:text-indigo-300 font-semibold">Stray FIP</div>
                <div className="text-2xl font-bold text-indigo-800 dark:text-indigo-200" data-testid="preview-stray">{preview.stray_framed_ip}</div>
                <div className="text-[10px] text-indigo-600 dark:text-indigo-400">Framed-IP salah</div>
              </div>
            </div>

            {preview.per_branch && preview.per_branch.length > 0 && (
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-2 max-h-32 overflow-y-auto">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-1">Breakdown per Cabang</div>
                <table className="w-full text-xs">
                  <tbody>
                    {preview.per_branch.map((b, i) => (
                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-1 pr-2 text-slate-700 dark:text-slate-300">{b.branch_name}</td>
                        <td className="py-1 pr-2 text-slate-500 dark:text-slate-400 text-[10px]">{b.payment_type}</td>
                        <td className="py-1 pr-2 text-right font-mono text-rose-700 dark:text-rose-300">{b.count}</td>
                        <td className="py-1 text-right font-mono text-slate-600 dark:text-slate-400">Rp {Number(b.total_amount).toLocaleString('id-ID')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview.customers && preview.customers.length > 0 && (
              <div className="rounded-lg bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-800 p-2 max-h-64 overflow-y-auto">
                <div className="flex items-center justify-between mb-1 sticky top-0 bg-white dark:bg-slate-900 py-1">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 dark:text-rose-300 font-semibold">
                    Daftar Pelanggan Tertunggak ({preview.customers.length})
                  </div>
                  <div className="text-[10px] text-slate-400">Klik nama untuk edit</div>
                </div>
                <table className="w-full text-xs" data-testid="preview-customer-list">
                  <thead className="sticky top-6 bg-slate-100 dark:bg-slate-800">
                    <tr className="text-[10px] text-slate-600 dark:text-slate-400 uppercase">
                      <th className="py-1 px-1 text-left">Nama</th>
                      <th className="py-1 px-1 text-left">No Akun</th>
                      <th className="py-1 px-1 text-left">Cabang</th>
                      <th className="py-1 px-1 text-right">Tunggakan</th>
                      <th className="py-1 px-1 text-center">Jatuh Tempo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.customers.map((cust) => (
                      <tr
                        key={cust.id}
                        className="border-t border-slate-100 dark:border-slate-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 cursor-pointer transition-colors"
                        onClick={() => {
                          onOpenChange(false);
                          if (typeof onEditCustomer === 'function') {
                            onEditCustomer(cust.id);
                          } else {
                            window.location.href = `/app/customers?edit=${cust.id}`;
                          }
                        }}
                        data-testid={"preview-customer-row-" + cust.id}
                      >
                        <td className="py-1 px-1 text-slate-800 dark:text-slate-200 font-medium truncate max-w-[130px]" title={cust.name}>{cust.name}</td>
                        <td className="py-1 px-1 text-slate-600 dark:text-slate-400 font-mono text-[10px]">{cust.account_number}</td>
                        <td className="py-1 px-1 text-slate-500 dark:text-slate-400 text-[10px]">{cust.branch_name}</td>
                        <td className="py-1 px-1 text-right font-mono text-rose-700 dark:text-rose-300 font-semibold">Rp {Number(cust.total_tunggakan).toLocaleString('id-ID')}</td>
                        <td className="py-1 px-1 text-center text-[10px] text-slate-500">{cust.next_due_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result && (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-300 dark:border-emerald-700 p-3 space-y-1 text-xs" data-testid="scan-isolir-result">
                <div className="font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Hasil Eksekusi
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-emerald-700 dark:text-emerald-300">
                  <div>Prepaid corrected: <b>{result.corrected_prepaid}</b></div>
                  <div>Postpaid corrected: <b>{result.corrected_postpaid}</b></div>
                  <div>Qualified: <b>{result.qualified_for_isolir}</b></div>
                  <div>Isolir OK: <b className="text-emerald-800 dark:text-emerald-200">{result.isolir_ok}</b></div>
                  <div>Skip: <b>{result.isolir_skip}</b></div>
                  <div>Error: <b>{result.isolir_err}</b></div>
                </div>
                <div className="text-[10px] text-emerald-600 dark:text-emerald-400 pt-1">
                  Sanitizer bg: {result.sanitizer_triggered_bg ? 'triggered' : 'skip'}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-500 dark:text-slate-400 py-4 italic">Belum ada preview</div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9" data-testid="scan-isolir-close-btn">Tutup</Button>
          <Button onClick={fetchPreview} disabled={loading || running} variant="outline" className="h-9" data-testid="scan-isolir-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={runScanIsolir} disabled={running || loading || total === 0} className="h-9 bg-rose-600 hover:bg-rose-700 text-white" data-testid="scan-isolir-run-btn">
            <ShieldOff className={`w-4 h-4 mr-1 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Memproses...' : `Jalankan Fix (${total})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Customers;
